import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const resources = {
  en: {
    translation: {
      // Navigation
      'nav.materials': 'Materials & Supplies',
      'nav.recipes': 'Dishes & Recipes',
      'nav.dashboard': 'Cost Dashboard',
      'nav.cogs': 'Dish Costing',
      'nav.pricing': 'Dish Pricing',
      'nav.applicationsPricing': 'Applications Pricing',
      'nav.fixedOverhead': 'Allocated Fixed Costs',
      'nav.variableOverhead': 'Allocated Variable Costs',
      'nav.userActions': 'User Actions',
      'nav.logout': 'Logout',
      'nav.activeUser': 'Active User',
      
      // Common
      'common.add': 'Add',
      'common.edit': 'Edit',
      'common.delete': 'Delete',
      'common.save': 'Save',
      'common.saving': 'Saving...',
      'common.cancel': 'Cancel',
      'common.close': 'Close',
      'common.ok': 'OK',
      'common.search': 'Search',
      'common.actions': 'Actions',
      'common.id': 'ID',
      'common.name': 'Name',
      'common.category': 'Category',
      'common.quantity': 'Quantity',
      'common.price': 'Price',
      'common.unit': 'Unit',
      'common.cost': 'Cost',
      'common.total': 'Total',
      'common.currency': 'Currency',
      'common.in': 'in',
      
      // Materials
      'materials.title': 'Materials & Supplies',
      'materials.allTitle': 'All Materials & Supplies',
      'materials.noMaterialsInCategory': 'No materials in this category',
      'materials.addFirstMaterial': 'Add your first material to get started',
      'materials.addNew': 'Add New Material',
      'materials.editMaterial': 'Edit Material',
      'materials.materialId': 'Material ID',
      'materials.nameEn': 'Material Name (EN)',
      'materials.nameAr': 'Material Name (AR)',
      'materials.brandName': 'Brand Name',
      'materials.mainUnit': 'Main Unit',
      'materials.quantityPerMainUnit': 'Quantity per Main Unit',
      'materials.quantityPerPiece': 'Quantity per Piece',
      'materials.subUnit': 'Sub Unit',
      'materials.priceOfMainUnit': 'Price of Main Unit',
      'materials.priceWithVat': 'Price with VAT',
      'materials.supplier': 'Supplier',
      'materials.category': 'Category',
      'materials.unitCost': 'Unit Cost',
      'materials.includesVat': '(Includes VAT)',
      'materials.excelUpload': 'Excel Upload',
      'materials.excelImportExport': 'Excel Import/Export',
      'materials.importExcelFile': 'Import Excel File',
      'materials.importExcelDescription': 'Import your materials data from Excel',
      'materials.dropExcelFile': 'Drop your Excel file here',
      'materials.releaseToImport': 'Release to import your Excel file',
      'materials.dragAndDropDescription': 'Drag and drop your Excel file here, or click to browse',
      'materials.exportMaterials': 'Export Materials',
      'materials.exporting': 'Exporting...',
      'materials.downloadTemplate': 'Download Template',
      'materials.downloadTemplateDescription': 'Get the Excel template with the required format',
      'materials.allergensColumn': 'Allergens Column',
      'materials.allergensColumnDescription': 'Enter allergen values separated by commas. Leave empty if no allergens.',
      'materials.validAllergenValues': 'Valid values:',
      'materials.allergenExamples': 'Examples:',
      'materials.importFile': 'Import File',
      'materials.importInProgress': 'Import in Progress...',
      'materials.preview': 'Preview',
      'materials.previewDescription': 'Review your data before importing',
      'materials.readyToImport': 'Ready to import',
      'materials.removeFile': 'Remove File',
      'materials.newCategories': 'New categories',
      'materials.existingCategories': 'Existing categories',
      'materials.unnamedMaterials': 'Unnamed materials',
      'materials.vatCalculations': 'VAT Calculations',
      'materials.vatCalculationMismatch': 'VAT calculation mismatch — corrected automatically',
      'materials.unnamed': 'Unnamed',
      'materials.brand': 'Brand',
      'materials.mainUnit': 'Main Unit',
      'materials.qtyMain': 'Qty/Main',
      'materials.qtyPiece': 'Qty/Piece',
      'materials.subUnit': 'Sub Unit',
      'materials.priceWithVat': 'Price with VAT',
      'materials.priceWithoutVat': 'Price without VAT',
      'materials.calories': 'Calories',
      'materials.serving': 'Serving',
      'materials.allergens': 'Allergens',
      'materials.uploadFile': 'Upload File',
      'materials.importingMaterials': 'Importing Materials',
      'materials.importingMaterialsDescription': 'Please wait while we process and save your materials...',
      'materials.successfullyUploaded': 'Successfully uploaded {{count}} materials!',
      'materials.caloriesWholeNumbers': 'Calories will be displayed as whole numbers',
      'materials.emptyCategoriesUnnamed': 'Empty categories will be assigned to "Unnamed"',
      'materials.allergensCommaSeparated': 'Allergens: comma-separated values (wheat, milk, eggs, etc.)',
      
      // CategoryRecipes
      'recipes.directMaterialCosts': 'Dishes & Recipes Management',
      'recipes.recipesInCategory': 'Dishes and Recipes in {{category}} category',
      'recipes.addNewRecipe': 'Add New Recipe',
      'recipes.backToCategories': 'Back to Categories',
      'recipes.loading': 'Loading...',
      'recipes.selectCategory': 'Select Category',
      'recipes.searchByRecipeNameOrId': 'Search by recipe name or ID',
      'recipes.sortById': 'Sort by ID',
      'recipes.sortByName': 'Sort by Name',
      'recipes.recipes': 'Dishes',
      'recipes.recipesTotal': '{{count}} dishes total',
      'recipes.noRecipesFound': 'No dishes found',
      'recipes.noRecipesInCategory': 'No dishes in this category',
      'recipes.noDishesAddedYet': 'No dishes have been added yet.',
      'recipes.tryAdjustingSearchTerms': 'Try adjusting your search terms',
      'recipes.addFirstRecipe': 'Add your first recipe to get started',
      'recipes.addFirstRecipeButton': 'Add First Recipe',
      'recipes.servings': 'servings',
      'recipes.totalCost': 'Total Cost',
      'recipes.totalCostPerServing': 'Total Cost Per Serving',
      'recipes.edit': 'Edit',
      'recipes.delete': 'Delete',
      'recipes.duplicate': 'Duplicate',
      'recipes.recipeDuplicatedSuccessfully': 'Recipe duplicated successfully!',
      'recipes.failedToDuplicateRecipe': 'Failed to duplicate recipe',
      'recipes.recipeUpdatedSuccessfully': 'Dish updated successfully! All dependent dishes have been recalculated.',
      'recipes.recipeCreatedSuccessfully': 'Recipe created successfully!',
      'recipes.recipeDeletedSuccessfully': 'Recipe deleted successfully!',
      'recipes.allDependentRecipesRecalculated': 'All dependent recipes have been recalculated.',
      'recipes.failedToLoadRecipesData': 'Failed to load dishes data',
      'recipes.failedToSaveRecipe': 'Failed to save recipe',
      'recipes.errorRecipeCannotIncludeSelf': 'A recipe cannot include itself as an ingredient.',
      'recipes.errorCircularRecipeDependency':
        'Cannot add recipe as ingredient: this would create a circular dependency.',
      'recipes.failedToDeleteRecipe': 'Failed to delete recipe',
      'recipes.confirmAction': 'Confirm Action',
      'recipes.confirmDeleteRecipe': 'Are you sure you want to delete this recipe?',
      'recipes.cannotDeleteRecipeUsedAsIngredient': 'Cannot delete recipe that is being used as ingredient in other recipes',
      
      // RecipeForm
      'recipeForm.recipeDetails': 'Dish Details',
      'recipeForm.basicInformation': 'Basic information about your dish',
      'recipeForm.enterDishNameEnglish': 'Enter dish name in English',
      'recipeForm.enterDishNameArabic': 'Enter dish name in Arabic',
      'recipeForm.selectCategory': 'Select a category',
      'recipeForm.portionUnit': 'Portion Unit',
      'recipeForm.unit': 'Unit',
      'recipeForm.recipeImage': 'Recipe Image',
      'recipeForm.chooseImage': 'Choose Image',
      'recipeForm.uploading': 'Uploading...',
      'recipeForm.ingredients': 'Ingredients',
      'recipeForm.directMaterialsLibrary': 'Direct Materials Library',
      'recipeForm.showDirectMaterialsLibrary': 'Show Direct Materials Library',
      'recipeForm.hideDirectMaterialsLibrary': 'Hide Direct Materials Library',
      'recipeForm.searchByRecipeNameOrId': 'Search by recipe name or id',
      'recipeForm.searchByMaterialNameOrId': 'Search by material name or id',
      'recipeForm.searchByCategoryNameOrId': 'Search by category name or id',
      'recipeForm.tip': 'Tip:',
      'recipeForm.clickSelectToAdd': 'Click "Select" on ingredients from the library to add them to your recipe',
      'recipeForm.clickSelectToAddDescription': 'Click "Select" on ingredients from the library above to add them to your recipe',
      'recipeForm.selectIngredient': 'Select ingredient',
      'recipeForm.selected': 'Selected',
      'recipeForm.material': 'Material',
      'recipeForm.recipe': 'Recipe',
      'recipeForm.quantity': 'Quantity',
      'recipeForm.addFirstIngredient': 'Add First Ingredient',
      'recipeForm.procedure': 'Procedure',
      'recipeForm.step': 'Step',
      'recipeForm.instruction': 'Instruction',
      'recipeForm.enterStepInstruction': 'Enter the step instruction...',
      'recipeForm.stepImage': 'Step Image',
      'recipeForm.viewFullImage': 'View Full Image',
      'recipeForm.removeImage': 'Remove Image',
      'recipeForm.uploadImage': 'Upload Image',
      'recipeForm.dragToReorder': 'Drag to reorder',
      'recipeForm.editQuantity': 'Edit quantity',
      'recipeForm.nutrition': 'Nutrition',
      'recipeForm.calories': 'Calories',
      'recipeForm.protein': 'Protein',
      'recipeForm.carbs': 'Carbs',
      'recipeForm.fat': 'Fat',
      'recipeForm.fiber': 'Fiber',
      'recipeForm.sugar': 'Sugar',
      'recipeForm.sodium': 'Sodium',
      'recipeForm.cholesterol': 'Cholesterol',
      'recipeForm.allergens': 'Allergens',
      'recipeForm.save': 'Save',
      'recipeForm.cancel': 'Cancel',
      'recipeForm.add': 'Add',
      'recipeForm.remove': 'Remove',
      'recipeForm.edit': 'Edit',
      'recipeForm.delete': 'Delete',
      'recipeForm.upload': 'Upload',
      'recipeForm.choose': 'Choose',
      'recipeForm.select': 'Select',
      'recipeForm.search': 'Search',
      'recipeForm.error': 'Error',
      'recipeForm.warning': 'Warning',
      'recipeForm.success': 'Success',
      'recipeForm.pleaseEnterInstruction': 'Please enter an instruction',
      'recipeForm.failedToLoadData': 'Failed to load materials and recipes data',
      'recipeForm.ingredient': 'Ingredient',
      'recipeForm.pleaseSelectImageFile': 'Please select an image file',
      'recipeForm.uploadCancelledByUser': 'Upload cancelled by user',
      'recipeForm.atLeastOneIngredientRequired': 'At least one ingredient is required for a new dish',
      'recipeForm.allStepsMustHaveEnglishInstructions': 'All procedure steps must have English instructions',
      'recipeForm.recipeFormSubmitTriggered': 'Recipe form submit triggered',
      'recipeForm.recipeFormValidationFailed': 'Recipe form validation failed',
      'recipeForm.recipeFormValidationPassed': 'Recipe form validation passed',
      'recipeForm.newStepPreview': 'New step preview',
      'recipeForm.recipePreview': 'Recipe preview',
      'recipeForm.stepPreview': 'Step preview',
      'recipeForm.stepImage': 'Step image',
      'recipeForm.holdingTemperature': 'Holding Temperature (°C)',
      'recipeForm.hasPricing': 'Has Pricing',
      'recipeForm.enablePricing': 'Enable Pricing',
      'recipeForm.enablePricingDescription': 'This dish will appear in the pricing page for setting selling prices and profit margins.',
      'recipeForm.disablePricingDescription': 'This dish will only be used as an ingredient in other dishes and won\'t appear in pricing calculations.',
      
      // Recipe Image Section
      'recipeForm.recipeImage': 'Dish Image',
      'recipeForm.addVisualRepresentation': 'Add a visual representation of your dish',
      'recipeForm.recipePreview': 'Dish preview',
      'recipeForm.uploaded': 'Uploaded',
      'recipeForm.currentImage': 'Current Image',
      'recipeForm.imageUploadedSuccessfully': 'Your dish image has been uploaded successfully.',
      'recipeForm.replaceImageDescription': 'You can replace this image by uploading a new one below.',
      'recipeForm.chooseImage': 'Choose Image',
      'recipeForm.uploading': 'Uploading...',
      'recipeForm.cancel': 'Cancel',
      'recipeForm.imageRequirements': 'Image Requirements',
      'recipeForm.formats': 'Formats',
      'recipeForm.maximumSize': 'Maximum size',
      'recipeForm.recommended': 'Recommended',
      'recipeForm.quality': 'Quality',
      'recipeForm.squareImagesRatio': 'Square images (1:1 ratio) for best display',
      'recipeForm.highResolutionImages': 'High resolution images work best',
      
      // Ingredients Tab
      'recipeForm.ingredientsLibrary': 'Ingredients Library',
      'recipeForm.showDirectMaterialsLibrary': 'Show Direct Materials Library',
      'recipeForm.hideDirectMaterialsLibrary': 'Hide Direct Materials Library',
      'recipeForm.allCategories': 'All Categories',
      'recipeForm.allMaterials': 'All Materials',
      'recipeForm.allDishes': 'All Dishes',
      'recipeForm.searchByDishNameOrId': 'Search by dish name or id',
      'recipeForm.searchByMaterialNameOrId': 'Search by material name or id',
      'recipeForm.searchByCategoryNameOrId': 'Search by category name or id',
      'recipeForm.select': 'Select',
      'recipeForm.materials': 'Materials',
      'recipeForm.noDishesInCategory': 'No dishes in this category',
      'recipeForm.tipClickSelect': '💡 Tip: Click "Select" on ingredients from the library to add them to your dish',
      'recipeForm.selectedIngredients': 'Selected Ingredients',
      'recipeForm.totalCost': 'Total Cost',
      'recipeForm.ingredient': 'ingredient',
      'recipeForm.ingredients': 'ingredients',
      'recipeForm.piece': 'piece',
      'recipeForm.waste': 'waste',
      'recipeForm.loadingAllergens': 'Loading allergens...',
      'recipeForm.noIngredientsAddedYet': 'No ingredients added yet',
      'recipeForm.clickSelectToAddIngredients': 'Click "Select" on ingredients from the library above to add them to your dish',
      'recipeForm.addAnIngredient': 'Add an Ingredient',
      'recipeForm.materialsDishes': 'Materials/Dishes',
      'recipeForm.selectIngredient': 'Select ingredient',
      'recipeForm.selected': 'Selected',
      'recipeForm.quantity': 'Quantity',
      'recipeForm.enterQuantity': 'Enter quantity',
      'recipeForm.wastePercentage': 'Waste Percentage',
      'recipeForm.enterWastePercentage': 'Enter waste percentage',
      'recipeForm.add': 'Add',
      'recipeForm.addFirstIngredient': 'Add your first ingredient',
      'recipeForm.addFirstIngredientDescription': 'Start building your dish by adding ingredients from the library above',
      
      // Add Ingredient Modal
      'recipeForm.addIngredient': 'Add Ingredient',
      'recipeForm.specifyQuantityFor': 'Specify quantity for',
      'recipeForm.quantity': 'Quantity',
      'recipeForm.quantityPlaceholder': 'e.g., 100',
      'recipeForm.wastePercentage': 'Waste Percentage',
      'recipeForm.wastePercentageDescription': '(prep loss, cooking loss, etc.)',
      'recipeForm.wastePercentagePlaceholder': 'e.g., 15',
      'recipeForm.youCanEditThisLater': 'You can edit this later.',
      'recipeForm.cancel': 'Cancel',
      'recipeForm.add': 'Add',
      'recipeForm.editQuantity': 'Edit Quantity',
      'recipeForm.updateQuantityFor': 'Update quantity for',
      'recipeForm.update': 'Update',
      'recipeForm.duplicateIngredient': 'Duplicate Ingredient',
      'recipeForm.ingredientAlreadyAdded': 'This ingredient is already added.',
      'recipeForm.ok': 'OK',
      
      // Procedure Tab
      'recipeForm.addProcedureSteps': 'Add Procedure Steps',
      'recipeForm.instruction': 'Instruction',
      'recipeForm.stepImage': 'Step Image',
      'recipeForm.uploading': 'Uploading...',
      'recipeForm.uploadImage': 'Upload Image',
      'recipeForm.stepPreview': 'Step preview',
      'recipeForm.imageSelected': 'Image selected',
      'recipeForm.removeImage': 'Remove Image',
      'recipeForm.addStep': 'Add Step',
      'recipeForm.steps': 'Steps',
      'recipeForm.noProcedureStepsAddedYet': 'No procedure steps added yet',
      'recipeForm.addStepsUsingFormAbove': 'Add steps using the form above',
      'recipeForm.procedureError': 'Procedure Error',
      'recipeForm.stepImageModal': 'Step Image',
      
      // Pricing
      'pricing.title': 'Dish Pricing',
      'pricing.subtitle': 'Manage pricing for your dishes and analyze profitability',
      'pricing.searchPlaceholder': 'Search by dish name or ID',
      'pricing.sortById': 'Sort by ID',
      'pricing.sortByName': 'Sort by Name',
      'pricing.allCategories': 'All Categories',
      'pricing.filterByCategory': 'Filter by category',
      'pricing.categoriesSelected': '{{count}} categories',
      'pricing.dishes': 'Dishes',
      'pricing.modifiedFromDefault': 'Modified from default cost per dish',
      'pricing.noRecipesFound': 'No dishes found',
      'pricing.noRecipesAvailable': 'No dishes available',
      'pricing.tryAdjustingSearchTerms': 'Try adjusting your search terms',
      'pricing.createFirstRecipe': 'Create your first dish to start pricing analysis',
      'pricing.createRecipe': 'Create Dish',
      'pricing.loadingRecipes': 'Loading recipes...',
      'pricing.id': 'ID',
      'pricing.dish': 'Dish',
      'pricing.dishCostPerServing': 'Dish Cost Per Serving',
      'pricing.fixedCost': 'Fixed Cost',
      'pricing.variableCost': 'Variable Cost',
      'pricing.totalCost': 'Total Cost',
      'pricing.profitMargin': 'Profit Margin',
      'pricing.sellingPrice': 'Selling Price',
      'pricing.marketPrice': 'Market Price',
      'pricing.pricingStatus': 'Pricing Status',
      'pricing.actions': 'Actions',
      'pricing.pricingSet': 'Pricing Set',
      'pricing.noPricing': 'No Pricing',
      'pricing.editPricing': 'Edit Pricing',
      'pricing.setPricing': 'Set Pricing',
      'pricing.fixed': 'Fixed',
      'pricing.variable': 'Variable',
      'pricing.total': 'Total',
      'pricing.margin': 'Margin %',
      'pricing.profitCost': 'Profit Cost',
      'pricing.ofCost': '% of Cost',
      'pricing.vsSellingPrice': 'vs Selling Price',
      'pricing.reset': 'Reset',
      'pricing.save': 'Save',
      'pricing.cancel': 'Cancel',
      'pricing.enterOneOfMarginOrPrice': 'Please enter at least one of: Margin %, Selling Price, Profit Cost, or % of Cost',
      'pricing.messages.enterOneOfMarginOrPrice': 'Please enter at least one of: Margin %, Selling Price, Profit Cost, or % of Cost',

      // Applications Pricing
      'applicationsPricing.title': 'Applications Pricing',
      'applicationsPricing.subtitle': 'Manage pricing applications and their percentage markups for dishes',
      'applicationsPricing.addApplication': 'Add Application',
      'applicationsPricing.addNewPricingApplication': 'Add New Pricing Application',
      'applicationsPricing.applicationName': 'Application Name',
      'applicationsPricing.applicationNamePlaceholder': 'e.g., Hangerstation, Locate, TheChefz',
      'applicationsPricing.percentageMarkup': 'Percentage Profit',
      'applicationsPricing.saving': 'Saving...',
      'applicationsPricing.save': 'Save',
      'applicationsPricing.cancel': 'Cancel',
      'applicationsPricing.applicationsList': 'Applications List',
      'applicationsPricing.searchByApplicationNameOrId': 'Search by application name or ID',
      'applicationsPricing.sortById': 'Sort by ID',
      'applicationsPricing.sortByName': 'Sort by Name',
      'applicationsPricing.pricingApplications': 'Pricing Applications',
      'applicationsPricing.noApplicationsFound': 'No applications found',
      'applicationsPricing.noPricingApplicationsFound': 'No pricing applications found',
      'applicationsPricing.tryAdjustingSearchCriteria': 'Try adjusting your search criteria',
      'applicationsPricing.createFirstPricingApplication': 'Create your first pricing application to start managing markup percentages',
      'applicationsPricing.percentage': 'Percentage Markup',
      'applicationsPricing.created': 'Created',
      'applicationsPricing.edit': 'Edit',
      'applicationsPricing.delete': 'Delete',
      'applicationsPricing.active': 'Active',
      'applicationsPricing.inactive': 'Inactive',
      'applicationsPricing.dishPricing': 'Dish Pricing',
      'applicationsPricing.showingDishesWithPricing': 'Showing dishes with current pricing and calculated prices with {{percentage}}% markup',
      'applicationsPricing.searchByDishNameOrId': 'Search by dish name or ID',
      'applicationsPricing.sortByPrice': 'Sort by Price ↑',
      'applicationsPricing.sortByPriceDesc': 'Sort by Price ↓',
      'applicationsPricing.currentSellingPrice': 'Current Selling Price',
      'applicationsPricing.applicationMarkup': 'Application Markup',
      'applicationsPricing.newSellingPrice': 'New Selling Price',
      'applicationsPricing.priceDifference': 'Price Difference',
      'applicationsPricing.noDishesFoundMatchingSearch': 'No dishes found matching your search',
      'applicationsPricing.noDishesWithPricingFound': 'No dishes with pricing found',
      'applicationsPricing.tryAdjustingSearchTerms': 'Try adjusting your search terms',
      'applicationsPricing.createRecipesAndSetPricing': 'Create recipes and set their pricing to see them here',
      'applicationsPricing.deletePricingApplication': 'Delete Pricing Application',
      'applicationsPricing.confirmDeleteApplication': 'Are you sure you want to delete the pricing application "{{name}}"?',
      'applicationsPricing.deleting': 'Deleting...',
      'applicationsPricing.deleteApplication': 'Delete Application',
      'applicationsPricing.loadingPricingApplications': 'Loading pricing applications...',
      'applicationsPricing.applicationNameRequired': 'Application name is required',
      'applicationsPricing.percentageMustBeBetween': 'Percentage must be between 0 and 1000',
      'applicationsPricing.failedToCreatePricingApplication': 'Failed to create pricing application',
      'applicationsPricing.failedToUpdatePricingApplication': 'Failed to update pricing application',
      'applicationsPricing.failedToToggleApplicationStatus': 'Failed to toggle application status',
      'applicationsPricing.failedToDeletePricingApplication': 'Failed to delete pricing application',
      'applicationsPricing.failedToLoadData': 'Failed to load data',

      // Settings Modal
      'settings.title': 'Settings',
      'settings.subtitle': 'Manage your application settings',
      'settings.themeSettings': 'Theme Settings',
      'settings.darkMode': 'Dark Mode',
      'settings.lightMode': 'Light Mode',
      'settings.darkThemeActive': 'Dark theme is currently active',
      'settings.lightThemeActive': 'Light theme is currently active',
      'settings.switchToLight': 'Switch to Light',
      'settings.switchToDark': 'Switch to Dark',
      'settings.languageSettings': 'Language Settings',
      'settings.switchToArabic': 'Switch to العربية',
      'settings.switchToEnglish': 'Switch to English',
      'settings.currentLanguage': 'Current language',

      // VAT Settings
      'vatSettings.title': 'VAT Settings',
      'vatSettings.vatPercentage': 'VAT Percentage',
      'vatSettings.edit': 'Edit',
      'vatSettings.save': 'Save',
      'vatSettings.cancel': 'Cancel',
      'vatSettings.vatPercentageMustBeBetween': 'VAT percentage must be between 0 and 100',
      'vatSettings.vatSettingsUpdatedSuccessfully': 'VAT settings updated successfully! All material prices have been recalculated.',
      'vatSettings.failedToUpdateVatSettings': 'Failed to update VAT settings',
      'vatSettings.changingVatPercentageWillRecalculate': 'Changing VAT percentage will automatically recalculate all material prices',
      'vatSettings.defaultVatRate': 'Default VAT rate: 15%',
      'vatSettings.pricesDisplayedWithVatIncluded': 'Prices are displayed with VAT included',
      
      // Logo Settings
      'logoSettings.title': 'Logo Settings',
      'logoSettings.uploadLogo': 'Upload Logo',
      'logoSettings.currentLogo': 'Current Logo',
      'logoSettings.logoPreview': 'Logo Preview (Not saved yet)',
      'logoSettings.selectFile': 'Select File',
      'logoSettings.uploading': 'Uploading...',
      'logoSettings.save': 'Save',
      'logoSettings.saving': 'Saving...',
      'logoSettings.cancel': 'Cancel',
      'logoSettings.removeLogo': 'Remove Logo',
      'logoSettings.pleaseSelectImageFile': 'Please select an image file',
      'logoSettings.imageSizeLimit': 'Image size must be less than 2MB',
      'logoSettings.logoUploadedSuccessfully': 'Logo uploaded successfully',
      'logoSettings.failedToUploadLogo': 'Failed to upload logo',
      'logoSettings.pleaseUploadLogoFirst': 'Please upload a logo first',
      'logoSettings.logoSavedSuccessfully': 'Logo saved successfully',
      'logoSettings.failedToSaveLogo': 'Failed to save logo',
      'logoSettings.logoRemovedSuccessfully': 'Logo removed successfully',
      'logoSettings.failedToRemoveLogo': 'Failed to remove logo',
      'logoSettings.logoUploadDescription': 'Upload your company or brand logo (optional)',
      'logoSettings.recommendedSize': 'Recommended size: 200x200px or higher',
      'logoSettings.supportedFormats': 'Supported formats: PNG, JPG, JPEG, SVG',
      'logoSettings.failedToPreviewImage': 'Failed to preview image',
      'logoSettings.noChangesToSave': 'No changes to save',
      'logoSettings.uploadCancelled': 'Upload cancelled',
      
      // Steps Section
      'recipeForm.viewFullImage': 'View Full Image',
      'recipeForm.removeImage': 'Remove Image',
      'recipeForm.save': 'Save',
      'recipeForm.cancel': 'Cancel',
      'recipeForm.edit': 'Edit',
      'recipeForm.delete': 'Delete',
      'recipeForm.areYouSureDeleteStep': 'Are you sure you want to delete this step?',
      'recipeForm.backToIngredients': 'Back to Ingredients',
      'recipeForm.recipeOverview': 'Recipe Overview',
      'recipeForm.backToRecipeOverview': 'Back to Recipe Overview',
      'recipeForm.nextProcedure': 'Next: Procedure',
      'recipeForm.nextIngredients': 'Next: Ingredients',
      'recipeForm.ingredients': 'Ingredients',
      'recipeForm.procedure': 'Procedure',
      'recipeForm.addAtLeastOneIngredientToCreateDish': 'Add at least one ingredient to create your dish.',
      'recipeForm.useFormAboveToAddIngredients': 'Use the form above to add ingredients.',
      'recipeForm.dragIngredientsFromLibraryToAddThem': 'Drag ingredients from the library to add them.',
      
      // Recipes
      'recipes.title': 'Dishes & Recipes Management',
      'recipes.subtitle': 'Select a category to view and manage its dishes and recipes',
      'recipes.addNewCategory': 'Add New Category',
      'recipes.addNewDish': 'Add New Dish',
      'recipes.editDish': 'Edit Dish',
      'recipes.recipeId': 'Recipe ID',
      'recipes.dishNameEn': 'Dish Name (EN)',
      'recipes.dishNameAr': 'Dish Name (AR)',
      'recipes.dishImage': 'Dish Image',
      'recipes.numberOfServings': 'Number of Servings',
      'recipes.servings': 'servings',
      'recipes.portionSize': 'Portion Size per Serving',
      'recipes.ingredients': 'Direct Materials',
      'recipes.addIngredient': 'Add Ingredient',
      'recipes.code': 'Code',
      'recipes.quantityUsed': 'Quantity Used',
      'recipes.type': 'Type',
      'recipes.rawMaterial': 'Raw Material',
      'recipes.recipe': 'Recipe',
      'recipes.totalDishCost': 'Total Dish Cost',
      'recipes.costPerServing': 'Total Cost per Serving',
      
      // Dashboard
      'dashboard.title': 'Cost Dashboard',
      'dashboard.dishCosts': 'Dish Costs per Serving',
      'dashboard.ingredientBreakdown': 'Direct Materials Breakdown',
      'dashboard.noMaterialsUsedInDishes': 'No materials are currently being used in dishes.',
      'dashboard.addMaterialsToSeeBreakdown': 'Add materials to dishes to see the cost breakdown chart here.',
      'dashboard.costDistribution': 'Cost Distribution',
      'dashboard.dishComparison': 'Dish Cost Comparison',
      'dashboard.noDishesForComparison': 'No dishes are currently available for comparison.',
      'dashboard.addDishesToSeeComparison': 'Add dishes to see the cost comparison chart here.',
      'dashboard.topMaterials': 'Top Used Materials',
      'dashboard.materialUsageStats': 'Material Usage Stats',
      'dashboard.topCostContributors': 'Top Cost Contributors',
      'dashboard.noMaterialsWithCostsFound': 'No materials with costs found',
      'dashboard.usageDistribution': 'Usage Distribution',
      'dashboard.costAnalysisAndInsights': 'Cost Analysis and Insights',
      'dashboard.totalMaterials': 'Total Materials',
      'dashboard.totalRecipes': 'Total Dishes',
      'dashboard.totalMonthlyFixedCosts': 'Total Monthly Fixed Costs',
      'dashboard.totalMonthlyVariableCosts': 'Total Monthly Variable Costs',
      'dashboard.breakEvenUnits': 'Break-even Units',
      'dashboard.breakEvenUnitsDescription': 'Number of dishes to break even',
      'dashboard.breakEvenSales': 'Break-even Sales',
      'dashboard.breakEvenSalesDescription': 'Value of sales to break even',
      'dashboard.breakEvenEqualDistributionNote': 'Expected orders are split equally across all dishes in each category.',
      'dashboard.dishes': 'dishes',
      'dashboard.units': 'units',
      'dashboard.totalDishes': 'Total Dishes',
      'dashboard.usedInDishes': 'Used in Dishes',
      'dashboard.highUsageDishes': 'High Usage (3+ dishes)',
      'dashboard.mediumUsageDishes': 'Medium Usage (1-2 dishes)',
      'dashboard.totalCostInDishes': 'Total Cost in Dishes',
      'dashboard.unusedMaterials': 'Unused Materials',
      'dashboard.avgCostPerMaterial': 'Avg Unit Cost of All Materials',
      'dashboard.totalFixedCosts': 'Total Monthly Fixed Costs',
      'dashboard.totalFixedAndVariableCosts': 'Total Fixed & Variable Costs',
      'dashboard.averageContributionMargin': 'Average Contribution Margin',
      'dashboard.breakEvenExcludedOrdersNote': '({{count}} expected orders from categories without priced dishes are excluded.)',
      'dashboard.breakEvenExcludedFixedCostsNote': '({{amount}} of monthly fixed costs from categories without priced dishes are excluded.)',
      'dashboard.breakEvenPartialPricingWarning': '{{category}}: {{priced}} of {{total}} dishes priced; orders are split across all dishes in this category.',
      'dashboard.totalExpectedOrders': 'Total Expected Orders',
      'dashboard.weightedAverageSellingPrice': 'Weighted Average Selling Price',
      'dashboard.totalContribution': 'Total Contribution',
      
      // Categories
      'categories.title': 'Categories',
      'categories.addNew': 'Add New Category',
      'categories.editCategory': 'Edit Category',
      'categories.nameEn': 'Category Name (EN)',
      'categories.nameAr': 'Category Name (AR)',
      'categories.image': 'Category Image',
      
      // Validation
      'validation.required': 'This field is required',
      'validation.invalidNumber': 'Please enter a valid number',
      'validation.duplicateIngredient': 'This ingredient is already added',
      'validation.materialInUse': 'Cannot delete material that is being used in recipes',
      'validation.recipeInUse': 'Cannot delete recipe that is being used as ingredient in other recipes',
      
      // Messages
      'messages.materialSaved': 'Material saved successfully',
      'messages.materialDeleted': 'Material deleted successfully',
      'messages.bulkMaterialsDeleted': '{{count}} materials deleted successfully!',
      'messages.recipeSaved': 'Recipe saved successfully',
      'messages.recipeDeleted': 'Recipe deleted successfully',
      'messages.bulkRecipesDeleted': '{{count}} recipes deleted successfully!',
      'messages.categorySaved': 'Category saved successfully',
      'messages.categoryDeleted': 'Category deleted successfully',
      'messages.bulkCategoriesDeleted': '{{count}} categories deleted successfully!',
      'messages.bulkMaterialsDeletedPartial': '{{count}} materials deleted successfully.',
      'messages.bulkMaterialsBlockedInRecipes': '{{count}} materials could not be deleted because they are being used in recipes.',
      'messages.bulkMaterialsOtherErrors': '{{count}} materials failed to delete due to other errors.',
      'messages.bulkMaterialsDeleteFailedPrefix': 'Failed to delete materials:',
      'messages.bulkMaterialsInRecipes': '{{count}} materials are being used in recipes.',
      'messages.bulkMaterialsOtherFailed': '{{count}} materials failed due to other errors.',
      'messages.bulkCategoriesDeletedPartial': '{{count}} categories deleted successfully.',
      'messages.bulkCategoriesContainMaterials': '{{count}} categories could not be deleted because they contain materials.',
      'messages.bulkCategoriesOtherErrors': '{{count}} categories failed to delete due to other errors.',
      'messages.bulkCategoriesDeleteFailedPrefix': 'Failed to delete categories:',
      'messages.bulkCategoriesContainMaterialsShort': '{{count}} categories contain materials.',
      'messages.bulkCategoriesOtherFailed': '{{count}} categories failed due to other errors.',
      'messages.bulkDeleteUnexpected': 'An unexpected error occurred during bulk delete. Please try again.',
      'messages.cannotDeleteCategoryContainsMaterials': 'Cannot delete category "{{name}}" because it contains {{count}} material(s). Please delete all materials in this category first, then try again.',
      'messages.cannotDeleteCategoryUsedInMaterials': 'Cannot delete category that is being used in materials. Please remove all materials from this category first.',
      'messages.cannotDeleteMaterialUsedInRecipes': 'Cannot delete material that is being used in recipes. Please remove it from all recipes first.',
      'messages.cannotDeleteMaterialUsedInRecipesWithCount': 'Cannot delete material that is being used in recipes. This material is used in {{count}} recipe(s).',
      'messages.failedDeleteMaterial': 'Failed to delete material',
      'messages.failedDeleteCategory': 'Failed to delete category',
      'messages.cannotDeleteCategoryContainsRecipes': 'Cannot delete category "{{name}}" because it contains {{count}} recipe(s). Please delete all recipes in this category first, then try again.',
      'messages.cannotDeleteCategoryUsedInRecipes': 'Cannot delete category that is being used in recipes. Please remove all recipes from this category first.',
      'messages.failedDeleteRecipe': 'Failed to delete recipe',
      'messages.failedUploadMaterials': 'Failed to upload materials',
      'messages.failedUploadMaterialsWithReason': 'Failed to upload materials: {{reason}}',
      'messages.failedCreateMaterial': 'Failed to create material',
      'messages.excelUploaded': 'Excel file uploaded successfully',
      'messages.error': 'An error occurred',

      // Authentication
      'auth.signIn': 'Sign In',
      'auth.signUp': 'Sign Up',
      'auth.signOut': 'Sign Out',
      'auth.createAccount': 'Create Account',
      'auth.haveAccount': 'Already have an account?',
      'auth.noAccount': "Don't have an account?",
      'auth.email': 'Email',
      'auth.username': 'Username',
      'auth.password': 'Password',
      'auth.confirmPassword': 'Confirm Password',
      'auth.emailPlaceholder': 'Enter your email',
      'auth.usernamePlaceholder': 'Enter your username',
      'auth.passwordPlaceholder': 'Enter your password',
      'auth.confirmPasswordPlaceholder': 'Confirm your password',
      'auth.phoneNumber': 'Phone Number',
      'auth.phoneNumberPlaceholder': 'Enter your phone number',
      'auth.country': 'Country',
      'auth.countryPlaceholder': 'Select your country',
      'auth.referralCode': 'Referral Code',
      'auth.referralCodePlaceholder': 'Enter referral code (optional)',
      'auth.referralCodeHint': 'Referral code will be applied to your account',
      'auth.optional': 'Optional',

      // Terms & Conditions
      'terms.agreeLabel': 'I have read and agree to the',
      'terms.linkText': 'Terms & Conditions',
      'terms.close': 'Close',
      'terms.mustAgree': 'You must agree to the Terms & Conditions to create an account',
      'terms.lastUpdatedLabel': 'Last updated',
      'terms.lastUpdatedValue': 'May 2026',
      'terms.userTitle': 'Terms & Conditions',
      'terms.userBody': [
        {
          heading: '1. Acceptance of Terms',
          body: 'By creating an account or using this application, you confirm that you have read, understood, and agree to be bound by these Terms & Conditions and our Privacy Policy. If you do not agree, please do not create an account or use the service.'
        },
        {
          heading: '2. Account & Eligibility',
          body: 'You must be at least 18 years old to create an account. You agree to provide accurate, current, and complete information during registration and to keep your account credentials confidential. You are responsible for all activity that occurs under your account. One account per person is permitted.'
        },
        {
          heading: '3. Subscription & Payment',
          body: 'Access to paid features requires an active yearly subscription. Plans are billed in advance for the yearly subscription period. All charges are non-refundable except where required by applicable law. Your subscription remains active for the paid period and can be renewed by completing a new payment. You are responsible for any applicable taxes.'
        },
        {
          heading: '4. Acceptable Use',
          body: 'You agree not to: (a) use the service for any unlawful purpose; (b) attempt to reverse engineer, decompile, or extract source code; (c) scrape, mirror, or resell access to the application; (d) share your account credentials with third parties; or (e) interfere with the security or normal operation of the service.'
        },
        {
          heading: '5. Data & Privacy',
          body: 'You retain ownership of the recipes, materials, pricing, and other data you input into the application. We store and process this data solely to provide and improve the service, in accordance with our Privacy Policy. We will not sell your business data to third parties.'
        },
        {
          heading: '6. Intellectual Property',
          body: 'The application, its user interface, source code, designs, logos, and documentation are owned by us and protected by intellectual property laws. Your input data remains yours. No part of the application may be copied, modified, or redistributed without our written permission.'
        },
        {
          heading: '7. Service Availability & Changes',
          body: 'The service is provided on an "as is" and "as available" basis. We do not guarantee uninterrupted or error-free operation. We may add, modify, or remove features, and may pause the service for maintenance with reasonable notice where practical.'
        },
        {
          heading: '8. Limitation of Liability',
          body: 'To the maximum extent permitted by law, we are not liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, revenue, data, or business opportunities. Our total aggregate liability shall not exceed the amount you paid us for your most recent active yearly subscription.'
        },
        {
          heading: '9. Termination',
          body: 'You may cancel your subscription and stop using the service at any time from your account settings. We may suspend or terminate accounts that violate these terms or engage in fraudulent, abusive, or illegal activity, with or without prior notice.'
        },
        {
          heading: '10. Governing Law & Contact',
          body: 'These terms are governed by the laws of the Kingdom of Saudi Arabia. Any disputes shall be resolved in the courts of Saudi Arabia. For questions or notices regarding these terms, please contact our support team through the application.'
        }
      ],
      'terms.affiliateTitle': 'Affiliate Program Terms & Conditions',
      'terms.affiliateBody': [
        {
          heading: '1. Acceptance & Eligibility',
          body: 'By joining the affiliate program you confirm that you have read and agree to these Affiliate Terms. You must be at least 18 years old, provide accurate identity and payout information, and you must not be a current employee, contractor, or immediate family member of an employee of this company.'
        },
        {
          heading: '2. Referral Mechanics',
          body: 'You will receive a unique referral code and/or link. Commissions are credited only on verified, paid subscriptions of new users who sign up using your referral code or link and complete payment. Self-referrals and referrals of existing users are not eligible.'
        },
        {
          heading: '3. Commission Rate & Tracking',
          body: 'The commission rate and any tiered structure are as published on your affiliate dashboard and may be updated from time to time with prior notice. Referrals are tracked via your referral code attribution at signup; we are not responsible for tracking failures caused by user actions, ad blockers, or third-party tools.'
        },
        {
          heading: '4. Payout Terms',
          body: 'Commissions become payable once your verified balance meets the minimum payout threshold. Payouts are processed on a monthly settlement cycle using the payout method you have selected and verified. You are solely responsible for any taxes, fees, or compliance obligations applicable to your payouts.'
        },
        {
          heading: '5. Prohibited Promotion',
          body: 'You may not: (a) send unsolicited bulk messages (spam); (b) bid on our brand keywords in paid search; (c) use cookie stuffing, forced clicks, or other deceptive tracking; (d) self-refer or use fake accounts; or (e) make misleading or unauthorized claims about the application, its features, or its results.'
        },
        {
          heading: '6. Brand Use',
          body: 'We grant you a limited, revocable, non-exclusive license to use our name, logo, and approved marketing assets solely to promote the application as part of this program. You may not modify our logo, imply employment or official endorsement, or register domains or social handles containing our brand.'
        },
        {
          heading: '7. Termination & Clawback',
          body: 'Either party may terminate this affiliate relationship at any time with written notice (in-app or email). If you misuse the system, abuse the affiliate program, create fake or misleading referrals, manipulate tracking, violate these terms, or engage in fraudulent or harmful activity, your affiliate account may be suspended or terminated immediately and you may become ineligible to receive any pending or unpaid commissions. Commissions earned on subscriptions that are later refunded, charged back, canceled within a refund window, or identified as fraudulent may be reversed and deducted from future or pending payouts.'
        },
        {
          heading: '8. Confidentiality',
          body: 'Non-public information made available through the affiliate dashboard (including conversion data, commission rates, and unreleased features) is confidential. You agree not to disclose it to third parties or use it outside the affiliate program.'
        },
        {
          heading: '9. Limitation of Liability & Indemnity',
          body: 'You agree to indemnify and hold us harmless from any claims, losses, or damages arising from your breach of these terms or your promotional activities. Our total aggregate liability to you is capped at the commissions paid to you for your most recent completed payout cycle.'
        },
        {
          heading: '10. Governing Law & Contact',
          body: 'These affiliate terms are governed by the laws of the Kingdom of Saudi Arabia. Any disputes shall be resolved in the courts of Saudi Arabia. For affiliate-related questions, please contact our affiliate support team through the affiliate dashboard.'
        }
      ],

      'auth.signInDescription': 'Sign in to your account',
      'auth.createAccountDescription': 'Create a new account',
      'auth.loginFailed': 'Login failed. Please check your credentials.',
      'auth.registrationFailed': 'Registration failed. Please try again.',
      'auth.logoutSuccess': 'Logged out successfully',
      'auth.verifyingEmail': 'Verifying your email...',
      'auth.emailVerified': 'Email Verified!',
      'auth.redirectingToDashboard': 'You will be redirected to the dashboard shortly...',
      'auth.goToDashboard': 'Go to Dashboard',
      'auth.goToLogin': 'Go to Login',
      'auth.emailVerification': 'Email Verification',
      'auth.checkEmailForVerification': 'Please check your email for a verification link. If you didn\'t receive the email, you can request a new one.',
      'auth.didntReceiveEmail': 'Didn\'t receive the email?',
      'auth.enterUserIdToResend': 'Enter your User ID to resend verification email:',
      'auth.enterUserId': 'Enter your User ID',
      'auth.sending': 'Sending...',
      'auth.resendVerificationEmail': 'Resend Verification Email',
      'auth.backToLogin': 'Back to Login',
      'auth.userIdRequired': 'User ID is required to resend verification email',
      'auth.unexpectedError': 'An unexpected error occurred',
      'auth.checkYourEmail': 'Check Your Email',
      'auth.verificationLinkSent': 'We\'ve sent a verification link to {{email}}. Please check your email and click the verification link to activate your account.',
      'auth.emailAlreadyVerified': 'Your email has already been verified.',
      'auth.verificationTokenAlreadyUsed': 'This verification link has already been used.',
      'auth.checkEmailForActivation': 'Please check your email for a verification link to activate your account.',
      'auth.checkSpamFolder': 'Check your spam folder or click the button below to resend the verification email.',
      'auth.userIdNotAvailable': 'User ID is not available',
      'auth.emailNotVerified': 'Please verify your email before logging in.',
      'auth.verificationEmailResent': 'Verification email resent successfully.',
      'auth.resendVerificationFailed': 'Failed to resend verification email.',
      'auth.forgotPassword': 'Forgot password?',
      'auth.forgotPasswordDescription': 'Enter your email and we will send you a password reset link.',
      'auth.sendResetLink': 'Send reset link',
      'auth.resetRequestSent': 'If an account with this email exists, a password reset link has been sent.',
      'auth.resetPassword': 'Reset password',
      'auth.resetPasswordDescription': 'Enter your new password below.',
      'auth.verifyingResetLink': 'Verifying your reset link...',
      'auth.invalidOrExpiredResetToken': 'Invalid or expired reset link.',
      'auth.passwordResetSuccess': 'Password reset successful. You can now log in.',
      'auth.passwordMinLength': 'Password must be at least 6 characters long.',
      'auth.passwordsDoNotMatch': 'Passwords do not match.',
      'auth.newPassword': 'New password',
      'auth.confirmNewPassword': 'Confirm new password',
      'auth.networkError': 'Network error. Please try again.',
      'auth.signingIn': 'Signing in...',
      'auth.loadingSignIn': 'Please wait while we sign you in',
      'auth.creatingAccount': 'Creating account...',
      'auth.passwordStrengthVeryWeak': 'Very Weak',
      'auth.passwordStrengthWeak': 'Weak',
      'auth.passwordStrengthFair': 'Fair',
      'auth.passwordStrengthGood': 'Good',
      'auth.passwordStrengthStrong': 'Strong',
      'auth.passwordStrengthLabel': 'Password strength',
      'auth.validationUsernameRequired': 'Username is required',
      'auth.validationUsernameMin': 'Username must be at least 3 characters',
      'auth.validationUsernameMax': 'Username must be less than 20 characters',
      'auth.validationUsernamePattern': 'Username can only contain letters, numbers, and underscores',
      'auth.validationEmailRequired': 'Email is required',
      'auth.validationEmailInvalid': 'Please enter a valid email address',
      'auth.validationPhoneRequired': 'Phone number is required',
      'auth.validationPhoneInvalid': 'Please enter a valid phone number',
      'auth.validationPhoneMin': 'Phone number must be at least 10 digits',
      'auth.validationPhoneMax': 'Phone number must be less than 16 digits',
      'auth.validationCountryRequired': 'Country is required',
      'auth.validationPasswordRequired': 'Password is required',
      'auth.validationPasswordMin': 'Password must be at least 6 characters',
      'auth.validationConfirmPasswordRequired': 'Please confirm your password',
      'auth.validationPasswordsMismatch': 'Passwords do not match',
      'affiliateAuth.signIn': 'Affiliate Sign In',
      'affiliateAuth.signInDescription': 'Sign in to your affiliate account',
      'affiliateAuth.noAccount': "Don't have an affiliate account?",
      'affiliateAuth.haveAccount': 'Already have an affiliate account?',
      'affiliateAuth.createAccount': 'Create Affiliate Account',
      'affiliateAuth.createAccountDescription': 'Join our affiliate program and start earning',
      'affiliateAuth.error.emailNotVerified': 'Please verify your email before logging in. Check your email for a verification link.',
      'affiliateAuth.error.pendingApproval': 'Your email is verified, but your affiliate account is pending admin approval.',
      'affiliateAuth.error.rejected': 'Your affiliate account was rejected. Please contact support for details.',
      'affiliateAuth.error.suspended': 'Your affiliate account is suspended. Please contact support.',
      'affiliateAuth.error.notActive': 'Your affiliate account is not active. Please contact support.',
      'affiliateDashboard.title': 'Affiliate Dashboard',
      'affiliateDashboard.welcome': 'Welcome back, {{username}}!',
      'affiliateDashboard.loading': 'Loading dashboard...',
      'affiliateDashboard.loadError': 'Failed to load dashboard data. Please try again.',
      'affiliateDashboard.nav.dashboard': 'Dashboard',
      'affiliateDashboard.nav.payoutDetails': 'Payout Details',
      'affiliateDashboard.expandSidebar': 'Expand Sidebar',
      'affiliateDashboard.collapseSidebar': 'Collapse Sidebar',
      'affiliateDashboard.affiliate': 'Affiliate',
      'affiliateDashboard.affiliateFallback': 'Affiliate',
      'affiliateDashboard.userActions': 'User Actions',
      'affiliateDashboard.totalReferrals': 'Total Referrals',
      'affiliateDashboard.verified': 'Verified',
      'affiliateDashboard.totalEarnings': 'Total Earnings',
      'affiliateDashboard.pendingEarnings': 'Pending Earnings',
      'affiliateDashboard.totalPendingEarnings': 'Total Pending Earnings',
      'affiliateDashboard.totalConfirmedEarnings': 'Total Confirmed Earnings',
      'affiliateDashboard.totalPaidEarnings': 'Total Paid Earnings',
      'affiliateDashboard.yourReferralLink': 'Your Referral Link',
      'affiliateDashboard.referralLinkSubtitle': 'Share your referral code or link to invite new users and track your earnings.',
      'affiliateDashboard.commissionClarification': 'for each registered user',
      'affiliateDashboard.referralCode': 'Referral Code',
      'affiliateDashboard.referralLink': 'Referral Link',
      'affiliateDashboard.commissionRate': 'Commission Rate',
      'affiliateDashboard.totalClicks': 'Total Clicks',
      'affiliateDashboard.conversionRate': 'Conversion Rate',
      'affiliateDashboard.clickAnalytics30Days': 'Click Analytics (Last 30 Days)',
      'affiliateDashboard.recentReferrals': 'Recent Referrals',
      'affiliateDashboard.user': 'User',
      'affiliateDashboard.statusLabel': 'Status',
      'affiliateDashboard.commission': 'Commission',
      'affiliateDashboard.referralDate': 'Referral Date',
      'affiliateDashboard.confirmedDate': 'Confirmed Date',
      'affiliateDashboard.paidDate': 'Paid Date',
      'affiliateDashboard.date': 'Date',
      'affiliateDashboard.commissionHistory': 'Commission History',
      'affiliateDashboard.amount': 'Amount',
      'affiliateDashboard.paymentMethod': 'Payment Method',
      'affiliateDashboard.noReferralsYet': 'No referrals yet. Share your referral link to start earning!',
      'affiliateDashboard.noCommissionHistory': 'No commission history yet.',
      'affiliateDashboard.status.pending': 'Pending',
      'affiliateDashboard.status.confirmed': 'Confirmed',
      'affiliateDashboard.status.paid': 'Paid',
      'affiliatePayout.title': 'Payout Details',
      'affiliatePayout.subtitle': 'Configure how you want to receive your commission payments',
      'affiliatePayout.loading': 'Loading payout details...',
      'affiliatePayout.loadError': 'Failed to load payout details. Please try again.',
      'affiliatePayout.saveError': 'Failed to save payout details. Please try again.',
      'affiliatePayout.savedSuccess': 'Payout details saved successfully!',
      'affiliatePayout.preferredPaymentMethod': 'Preferred Payment Method',
      'affiliatePayout.bankTransfer': 'Bank Transfer',
      'affiliatePayout.bankTransferDescription': 'Receive payments directly to your bank account',
      'affiliatePayout.paypal': 'PayPal',
      'affiliatePayout.paypalDescription': 'Receive payments via PayPal',
      'affiliatePayout.bankAccountDetails': 'Bank Account Details',
      'affiliatePayout.accountHolderName': 'Account Holder Name',
      'affiliatePayout.accountHolderPlaceholder': 'John Doe',
      'affiliatePayout.bankName': 'Bank Name',
      'affiliatePayout.bankNamePlaceholder': 'Example Bank',
      'affiliatePayout.accountNumber': 'Account Number',
      'affiliatePayout.bankCountry': 'Bank Country',
      'affiliatePayout.selectCountry': 'Select country',
      'affiliatePayout.iban': 'IBAN',
      'affiliatePayout.swiftOptional': 'SWIFT/BIC Code (Optional)',
      'affiliatePayout.paypalDetails': 'PayPal Details',
      'affiliatePayout.paypalEmail': 'PayPal Email Address',
      'affiliatePayout.saving': 'Saving...',
      'affiliatePayout.savePayoutDetails': 'Save Payout Details',
      'common.noResults': 'No results',
      'common.error': 'Error',
      'common.tryAgain': 'Try again',
      'common.copy': 'Copy',
      'common.copied': 'Copied!',
      'common.na': 'N/A',
      'common.all': 'All',
      'common.month': 'Month',
      'common.closeMenu': 'Close Menu',
      'common.toggleMenu': 'Toggle menu',
      'auth.subscriptionRequired': 'Subscription required',
      'auth.subscriptionRequiredMessage': 'To continue using the app, you will pay 999 SAR for the first year, then 299 SAR every year after that.',
      'auth.subscriptionRequiredMessageRenewal': 'To continue using the app, you will pay 299 SAR per year.',
      'auth.annualPlan': 'Annual plan',
      'auth.subscriptionDetails': 'Your subscription plan',
      'auth.subscriptionClearCopy': 'You pay one price for the first year, then a lower fixed price every year after that.',
      'auth.firstYearLabel': 'First year (pay today)',
      'auth.firstYearDesc': 'One-time payment for the first 12 months.',
      'auth.afterFirstYearLabel': 'After the first year',
      'auth.afterFirstYearDescPrefix': 'Automatically renews at',
      'auth.afterFirstYearDescSuffix': 'per year until you cancel.',
      'auth.subscriptionExpired': 'Subscription expired',
      'auth.subscriptionExpiredMessage': 'Your subscription has expired. Please renew to continue using the app.',
      'auth.payNow': 'Pay now',
      'auth.sessionExpired': 'Session expired. Please log in again.',
      'auth.completeSubscription': 'Complete your annual subscription to use the app.',
      'auth.loading': 'Loading...',
      'auth.apply': 'Apply',
      'auth.havePromo': 'Have a promo code?',
      'auth.enterPromo': 'Enter promo code',
      'auth.invalidPromo': 'Invalid or expired promo code.',
      'auth.promoApplied': 'Promo applied: save {{amount}} {{currency}}',
      'auth.promoOverridesSale': 'Promo code overrides the active sale.',
      'auth.activeSaleCampaign': 'Active sale campaign',
      'auth.saleSavingsYearly': 'Save {{amount}} {{currency}} on yearly subscription',
      'auth.forOneYear': 'for 1 year',
      'auth.noAutoRenewal': 'No auto-renewal. You can re-subscribe next year.',
      'auth.subscriptionAnnualBilled': 'You pay {{amount}} {{currency}} once for 1 year.',
      'auth.paymentSuccess': 'Payment successful',
      'auth.paymentSuccessMessage': 'Your subscription is active. Redirecting to dashboard...',
      'auth.paymentFailed': 'Payment failed or cancelled',
      'auth.paymentFailedMessage': 'Your payment could not be completed. You can try again from the dashboard.',
      'auth.tryAgain': 'Try again',
      'auth.paymentResult': 'Payment result',
      'auth.paymentResultMessage': 'If you completed a payment, your subscription should be active. Go to the dashboard to continue.',
      
      // Pricing
      'pricing.title': 'Dish Pricing',
      'pricing.tab.pricing': 'Pricing',
      'pricing.inputs.rawMaterial': 'Raw Material Cost',
      'pricing.inputs.fixedCost': 'Fixed Cost',
      'pricing.inputs.variableCost': 'Variable Cost',
      'pricing.inputs.profitMargin': 'Profit Margin (%)',
      'pricing.inputs.sellingPrice': 'Selling Price',
      'pricing.inputs.marketPrice': 'Market Price',
      'pricing.actions.reset': 'Reset',
      'pricing.actions.save': 'Save Pricing',
      'pricing.actions.openInsights': 'Open Profitability Insights',
      'pricing.totals.totalCost': 'Total Cost',
      'pricing.totals.rawMaterialPercent': 'Raw Material %',
      'pricing.totals.fixedCostPercent': 'Fixed Cost %',
      'pricing.totals.variableCostPercent': 'Variable Cost %',
      'pricing.messages.enterOneOfMarginOrPrice': 'Please enter either a profit margin or a selling price to calculate.',
      'pricing.messages.negativeMargin': 'Costs exceed selling price (negative profit margin).',
      'pricing.messages.pricingSaved': 'Pricing saved successfully',
      'pricing.messages.pricingUpdated': 'Pricing updated successfully',
      'pricing.messages.pricingDeleted': 'Pricing deleted successfully',
      'pricing.marketComparison.above': 'Above market',
      'pricing.marketComparison.below': 'Below market',
      'pricing.marketComparison.equal': 'Equal to market',
      'pricing.validation.marginRequired': 'Please enter a profit margin',
      'pricing.validation.priceRequired': 'Please enter a selling price',
      'pricing.validation.invalidMargin': 'Profit margin must be between 0% and 95%',
      'pricing.validation.invalidPrice': 'Selling price must be greater than 0',
      
      // Currency Settings
      'currency.title': 'Currency Settings',
      'currency.current': 'Current Currency',
      'currency.select': 'Select Currency',
      'currency.preview': 'Format Preview',
      'currency.decimalPlaces': 'Decimal places',
      'currency.example': 'Example',
      
      // Additional common translations
      'materials.subtitle': 'Select a category to view and manage its materials & supplies',
      'common.materials': 'Materials',
      'materials.materials': 'Materials',
      'common.allCategories': 'All Categories',
      'common.allMaterials': 'All Materials',
      'common.allRecipes': 'All Dishes',
      'common.dishes': 'Dishes',
      'common.dish': 'Recipe',
      'common.categoriesTotal': 'categories total',
      'common.materialsTotal': 'materials total',
      'common.clickToViewMaterials': 'Click to view materials',
      'common.clickToViewDetails': 'Click to view details',
      'common.none': 'None',
      'common.noAllergens': 'No allergens',
      'common.noAllergensFound': 'No allergens found matching "{searchTerm}"',
      'common.allergens': 'allergens',
      'common.allergen': 'Allergen',
      'common.loading': 'Loading',
      'common.error': 'Error',
      'common.success': 'Success',
      'common.print': 'Print',
      'common.at': 'at',
      'common.goTo': 'Go to',
      'common.noCategoriesFound': 'No categories found',
      'common.noCategoriesYet': 'No categories yet',
      'common.tryAdjustingSearch': 'Try adjusting your search terms',
      'common.createFirstCategory': 'Create First Category',
      'common.deleteCategory': 'Delete Category',
      'common.cannotDeleteCategory': 'Cannot Delete Category',
      'common.deleteMaterial': 'Delete Material',
      'common.deleteRecipe': 'Delete Recipe',
      'common.confirmDeleteCategory': 'Are you sure you want to delete the category "{{name}}"?',
      'common.confirmDeleteMaterial': 'Are you sure you want to delete the material "{{name}}"?',
      'common.confirmDeleteRecipe': 'Are you sure you want to delete the recipe "{{name}}"?',
      'common.confirmDeleteSelectedMaterials': 'Are you sure you want to delete {{count}} selected materials? This action cannot be undone.',
      'common.confirmDeleteSelectedCategories': 'Are you sure you want to delete {{count}} selected categories? This action cannot be undone.',
      'common.confirmDeleteSelectedRecipes': 'Are you sure you want to delete {{count}} selected recipes? This action cannot be undone.',
      'common.language': 'Language',
      'common.switchToArabic': 'Switch to Arabic',
      'common.switchToEnglish': 'Switch to English',
      
      // Allergens
      'allergens.wheat': 'Wheat',
      'allergens.wheat.examples': 'Containing gluten, such as: wheat, rye, barley, oats',
      'allergens.fish': 'Fish and Products',
      'allergens.fish.examples': 'Such as: fish meat, fish oil',
      'allergens.peanuts': 'Peanuts and Products',
      'allergens.peanuts.examples': 'Such as: peanut butter',
      'allergens.tree_nuts': 'Tree Nuts and Products',
      'allergens.tree_nuts.examples': 'Such as: cashews, pistachios, and others',
      'allergens.milk': 'Milk and Products',
      'allergens.milk.examples': '(Including lactose) such as: milk, yogurt, cheese, cream, butter',
      'allergens.celery': 'Celery and Products',
      'allergens.celery.examples': 'Such as: celery seeds, celery salt',
      'allergens.crustaceans': 'Crustaceans and Products',
      'allergens.crustaceans.examples': 'Such as: shrimp',
      'allergens.eggs': 'Eggs and Products',
      'allergens.eggs.examples': 'Such as: powdered eggs',
      'allergens.mustard': 'Mustard and Products',
      'allergens.mustard.examples': 'Such as: mustard seeds, mustard oil, mustard sauce',
      'allergens.sesame': 'Sesame Seeds and Products',
      'allergens.sesame.examples': 'Such as: sesame oil',
      'allergens.mollusks': 'Mollusks and Products',
      'allergens.mollusks.examples': 'Such as: shellfish (clams, scallops)',
      'allergens.lupin': 'Lupin and Products',
      'allergens.lupin.examples': 'Such as: lupin flour',
      'allergens.soybeans': 'Soybeans and Products',
      'allergens.soybeans.examples': 'Such as: soy milk',
      'allergens.sulfites': 'Sulfur Dioxide and Sulfites',
      'allergens.sulfites.examples': 'At concentrations greater than 10 mg/kg or more',
      
      // Allergen shortcuts
      'allergens.short.wheat': 'Wheat',
      'allergens.short.fish': 'Fish',
      'allergens.short.peanuts': 'Peanuts',
      'allergens.short.tree_nuts': 'Tree Nuts',
      'allergens.short.milk': 'Milk',
      'allergens.short.celery': 'Celery',
      'allergens.short.crustaceans': 'Crustaceans',
      'allergens.short.eggs': 'Eggs',
      'allergens.short.mustard': 'Mustard',
      'allergens.short.sesame': 'Sesame',
      'allergens.short.mollusks': 'Mollusks',
      'allergens.short.lupin': 'Lupin',
      'allergens.short.soybeans': 'Soybeans',
      'allergens.short.sulfites': 'Sulfites',
      
      // Material Details
      'materials.materialOverview': 'Material Overview',
      'materials.basicInformation': 'Basic Information',
      'materials.nutritionalInformation': 'Nutritional Information',
      'materials.specifications': 'Specifications',
      'materials.costBreakdown': 'Cost Breakdown',
      'materials.usageStatistics': 'Usage Statistics',
      'materials.recipeDetails': 'Recipe Details',
      'materials.mainUnit': 'Main Unit',
      'materials.subUnit': 'Sub Unit',
      'materials.qtyPerMainUnit': 'Qty per Main Unit',
      'materials.qtyPerPiece': 'Qty per Piece',
      'materials.priceWithoutVat': 'Price without VAT',
      'materials.priceWithVat': 'Price with VAT',
      'materials.calculatedUnitCost': 'Calculated Unit Cost',
      'materials.usedInRecipes': 'Used in Dishes',
      'materials.totalQuantityUsed': 'Total Quantity Used',
      'materials.primaryUnitOfMeasurement': 'Primary unit of measurement',
      'materials.secondaryUnitOfMeasurement': 'Secondary unit of measurement',
      'materials.quantityInMainUnit': 'Quantity in main unit',
      'materials.quantityPerIndividualPiece': 'Quantity per individual piece',
      'materials.priceExcludingVat': 'Price excluding VAT',
      'materials.priceIncludingVat': 'Price including VAT (15%)',
      'materials.costPerSmallestUnit': 'Cost per smallest unit',
      'materials.howThisMaterialIsUsed': 'How this material is used across recipes',
      'materials.basicInformationAndSpecifications': 'Basic information and specifications',
      'materials.measurementAndQuantityDetails': 'Measurement and quantity details',
      'materials.detailedCostAnalysisAndCalculations': 'Detailed cost analysis and calculations',
      'materials.available': 'Available',
      'materials.noRecipesFound': 'No dishes found',
      'materials.noMaterialsFound': 'No materials found',
      'materials.noMaterialsAddedYet': 'No materials have been added yet.',
      'materials.generatedOn': 'Generated on',
      'materials.dishCostingSystem': 'Dish Costing System - Material Details',
      'materials.pageOf': 'Page 1 of 1',
      'common.edit': 'Edit',
      'common.delete': 'Delete',
      'common.searchByDishNameOrId': 'Search by dish name or ID',
      'common.searchByCategoryNameOrId': 'Search by category name or ID',
      'common.searchByMaterialNameOrId': 'Search by material name or ID',
      'common.materialInformation': 'Material Information',
      'common.basicDetails': 'Basic details about the material',
      'common.nutritionalInformation': 'Nutritional Information',
      'common.calorieAndServingInfo': 'Calorie and serving information',
      'common.servingSize': 'Serving Size',
      'common.caloriesPerServing': 'Calories (per serving)',
      'common.allergens': 'Allergens',
      'common.selectAllergens': 'Select allergens...',
      'common.materialImage': 'Material Image',
      'common.uploadImage': 'Upload Image',
      'common.uploading': 'Uploading...',
      'common.cancel': 'Cancel',
      'common.removeImage': 'Remove image',
      'common.selectUnit': 'Select unit',
      'common.selectCategory': 'Select category',
      'common.selectCategoryForDeletion': 'Select category for deletion',
      'common.enterPriceExcludingVat': 'Enter price excluding VAT',
      'common.calculatedAutomatically': 'Calculated automatically',
      'common.pleaseSelectImageFile': 'Please select an image file',
      'common.imageSizeLimit': 'Image size should be less than 2MB',
      'common.failedToPreviewImage': 'Failed to preview image',
      'common.failedToUploadImage': 'Failed to upload image. Please try again.',
      'common.pleaseEnterValidPositiveNumber': 'Please enter a valid positive number',
      'common.pleaseEnterValidNumber': 'Please enter a valid number (can be 0 or positive)',
      'common.pleaseEnterValidNumberZeroOrPositive': 'Please enter a valid number (0 or positive)',
      'common.thisDefinesBaseUnit': 'This defines the base unit for which the calories are measured',
      'common.willBeDisplayedAsWholeNumbers': 'Will be displayed as whole numbers (e.g., 18.5 → 19 kcal)',
      'common.selectAllergensPresent': 'Select any allergens present in this material. This will automatically flag any recipes that use this material.',
      
      // Bulk delete
      'common.selectAll': 'Select All',
      'common.deselectAll': 'Deselect All',
      'common.deleteSelected': 'Delete Selected',
      'common.deleteSelectedMaterialsCount': 'Delete {{count}} Materials',
      'common.deleteSelectedCategoriesCount': 'Delete {{count}} Categories',
      'common.bulkDelete': 'Bulk Delete',
      'common.confirmBulkDelete': 'Confirm Bulk Delete',

      // Allocated Fixed Costs
      'allocatedFixedCosts.title': 'Allocated Fixed Costs',
      'allocatedFixedCosts.description': 'Manage allocated fixed expense items and distribute costs by category.',
      'allocatedFixedCosts.enterItems': 'Enter Allocated Fixed Expense Items',
      'allocatedFixedCosts.distributeCosts': 'Distribute Allocated Fixed Costs by Category',
      'allocatedFixedCosts.addNewItem': 'Add New Item',
      'allocatedFixedCosts.editItem': 'Edit Fixed Expense Item',
      'allocatedFixedCosts.itemNameEn': 'Item Name (EN)',
      'allocatedFixedCosts.itemNameAr': 'Item Name (AR)',
      'allocatedFixedCosts.itemNameEnPlaceholder': 'Enter item name in English',
      'allocatedFixedCosts.itemNameArPlaceholder': 'أدخل اسم العنصر بالعربية',
      'allocatedFixedCosts.monthlyValue': 'Monthly Value',
      'allocatedFixedCosts.yearlyValue': 'Yearly Value',
      'allocatedFixedCosts.monthlyValuePlaceholder': 'Enter monthly value',
      'allocatedFixedCosts.yearlyValuePlaceholder': 'Enter yearly value',
      'allocatedFixedCosts.fixedExpenseItems': 'Allocated Fixed Expense Items',
      'allocatedFixedCosts.totalMonthlyValue': 'Total Monthly Value',
      'allocatedFixedCosts.totalYearlyValue': 'Total Yearly Value',
      'allocatedFixedCosts.costDistributionResults': 'Cost Distribution Results',
      'allocatedFixedCosts.category': 'Category',
      'allocatedFixedCosts.categoryId': 'Category ID',
      'allocatedFixedCosts.allocatedPercentage': 'Allocated %',
      'allocatedFixedCosts.expectedOrders': 'Expected Orders',
      'allocatedFixedCosts.costPerDish': 'Cost per Dish (Monthly)',
      'allocatedFixedCosts.totalAllocated': 'Total Allocated',
      'allocatedFixedCosts.totalAllocationExceeds': 'Total allocation exceeds 100%',
      'allocatedFixedCosts.currentTotal': 'Current total',
      'allocatedFixedCosts.reduceAllocations': 'Please reduce allocations to stay within 100%.',
      'allocatedFixedCosts.edit': 'Edit',
      'allocatedFixedCosts.save': 'Save',
      'allocatedFixedCosts.cancel': 'Cancel',
      'allocatedFixedCosts.addItem': 'Add Item',
      'allocatedFixedCosts.updateItem': 'Update Item',
      'allocatedFixedCosts.saving': 'Saving...',
      'allocatedFixedCosts.adding': 'Adding...',
      'allocatedFixedCosts.deleteItem': 'Delete Item',
      'allocatedFixedCosts.deleteDistribution': 'Delete Distribution',
      'allocatedFixedCosts.deleting': 'Deleting...',
      'allocatedFixedCosts.deleteItemConfirm': 'Are you sure you want to delete the item "{itemName}"? This action cannot be undone.',
      'allocatedFixedCosts.deleteDistributionConfirm': 'Are you sure you want to delete the distribution for "{categoryName}"? This action cannot be undone.',
      'allocatedFixedCosts.searchByItemName': 'Search by item name...',
      'allocatedFixedCosts.searchByCategoryName': 'Search by category name...',
      'allocatedFixedCosts.sortById': 'Sort by ID',
      'allocatedFixedCosts.sortByName': 'Sort by Name',
      'allocatedFixedCosts.sortBy': 'Sort by',
      'allocatedFixedCosts.validationError': 'Validation Error',
      'allocatedFixedCosts.allocationLimitExceeded': 'Allocation Limit Exceeded',
      'allocatedFixedCosts.totalAllocatedCannotExceed': 'Total allocated percentage cannot exceed 100%. Current total would be {{total}}%',
      'allocatedFixedCosts.itemsList': 'Items List',

      // Allocated Variable Costs
      'allocatedVariableCosts.title': 'Allocated Variable Costs',
      'allocatedVariableCosts.description': 'Manage allocated variable expense items and distribute costs by category.',
      'allocatedVariableCosts.enterItems': 'Enter Allocated Variable Expense Items',
      'allocatedVariableCosts.distributeCosts': 'Distribute Allocated Variable Costs by Category',
      'allocatedVariableCosts.addNewItem': 'Add New Item',
      'allocatedVariableCosts.editItem': 'Edit Variable Expense Item',
      'allocatedVariableCosts.itemNameEn': 'Item Name (EN)',
      'allocatedVariableCosts.itemNameAr': 'Item Name (AR)',
      'allocatedVariableCosts.itemNameEnPlaceholder': 'Enter item name in English',
      'allocatedVariableCosts.itemNameArPlaceholder': 'أدخل اسم العنصر بالعربية',
      'allocatedVariableCosts.monthlyValue': 'Monthly Value',
      'allocatedVariableCosts.yearlyValue': 'Yearly Value',
      'allocatedVariableCosts.monthlyValuePlaceholder': 'Enter monthly value',
      'allocatedVariableCosts.yearlyValuePlaceholder': 'Enter yearly value',
      'allocatedVariableCosts.variableExpenseItems': 'Allocated Variable Expense Items',
      'allocatedVariableCosts.totalMonthlyValue': 'Total Monthly Value',
      'allocatedVariableCosts.totalYearlyValue': 'Total Yearly Value',
      'allocatedVariableCosts.costDistributionResults': 'Cost Distribution Results',
      'allocatedVariableCosts.category': 'Category',
      'allocatedVariableCosts.categoryId': 'Category ID',
      'allocatedVariableCosts.allocatedPercentage': 'Allocated %',
      'allocatedVariableCosts.expectedOrders': 'Expected Orders',
      'allocatedVariableCosts.costPerDish': 'Cost per Dish (Monthly)',
      'allocatedVariableCosts.totalAllocated': 'Total Allocated',
      'allocatedVariableCosts.totalAllocationExceeds': 'Total allocation exceeds 100%',
      'allocatedVariableCosts.currentTotal': 'Current total',
      'allocatedVariableCosts.reduceAllocations': 'Please reduce allocations to stay within 100%.',
      'allocatedVariableCosts.edit': 'Edit',
      'allocatedVariableCosts.save': 'Save',
      'allocatedVariableCosts.cancel': 'Cancel',
      'allocatedVariableCosts.addItem': 'Add Item',
      'allocatedVariableCosts.updateItem': 'Update Item',
      'allocatedVariableCosts.saving': 'Saving...',
      'allocatedVariableCosts.adding': 'Adding...',
      'allocatedVariableCosts.deleteItem': 'Delete Item',
      'allocatedVariableCosts.deleteDistribution': 'Delete Distribution',
      'allocatedVariableCosts.deleting': 'Deleting...',
      'allocatedVariableCosts.deleteItemConfirm': 'Are you sure you want to delete the item "{itemName}"? This action cannot be undone.',
      'allocatedVariableCosts.deleteDistributionConfirm': 'Are you sure you want to delete the distribution for "{categoryName}"? This action cannot be undone.',
      'allocatedVariableCosts.searchByItemName': 'Search by item name...',
      'allocatedVariableCosts.searchByCategoryName': 'Search by category name...',
      'allocatedVariableCosts.sortById': 'Sort by ID',
      'allocatedVariableCosts.sortByName': 'Sort by Name',
      'allocatedVariableCosts.sortBy': 'Sort by',
      'allocatedVariableCosts.validationError': 'Validation Error',
      'allocatedVariableCosts.allocationLimitExceeded': 'Allocation Limit Exceeded',
      'allocatedVariableCosts.totalAllocatedCannotExceed': 'Total allocated percentage cannot exceed 100%. Current total would be {{total}}%',
      'allocatedVariableCosts.itemsList': 'Items List',
      'allocatedVariableCosts.actions': 'Actions',
    }
  },
  ar: {
    translation: {
      // Navigation
      'nav.materials': 'المواد والمستلزمات',
      'nav.recipes': 'الأطباق والوصفات',
      'nav.dashboard': 'لوحة متابعة التكاليف',
      'nav.cogs': 'تسعير الأطباق',
      'nav.pricing': 'تسعير الأطباق',
      'nav.applicationsPricing': 'تسعير التطبيقات',
      'nav.fixedOverhead': 'توزيع التكاليف الثابتة',
      'nav.variableOverhead': 'تزويع التكاليف المتغيرة',
      'nav.userActions': 'إجراءات المستخدم',
      'nav.logout': 'تسجيل الخروج',
      'nav.activeUser': 'مستخدم نشط',
      
      // Common
      'common.add': 'إضافة',
      'common.edit': 'تعديل',
      'common.delete': 'حذف',
      'common.save': 'حفظ',
      'common.saving': 'جاري الحفظ...',
      'common.cancel': 'إلغاء',
      'common.close': 'إغلاق',
      'common.ok': 'موافق',
      'common.search': 'بحث',
      'common.actions': 'الإجراءات',
      'common.id': 'الرقم',
      'common.name': 'الاسم',
      'common.category': 'التصنيف',
      'common.quantity': 'الكمية',
      'common.price': 'السعر',
      'common.unit': 'الوحدة',
      'common.cost': 'التكلفة',
      'common.total': 'المجموع',
      'common.currency': '﷼',
      'common.in': 'في',
      
      // Materials
      'materials.title': 'المواد والمستلزمات',
      'materials.allTitle': 'كل المواد والمستلزمات',
      'materials.noMaterialsInCategory': 'لا توجد مواد في هذا التصنيف',
      'materials.addFirstMaterial': 'أضف أول مادة للبدء',
      'materials.addNew': 'إضافة مادة جديدة',
      'materials.editMaterial': 'تعديل المادة',
      'materials.materialId': 'رقم المادة',
      'materials.nameEn': 'اسم المادة (بالإنجليزية)',
      'materials.nameAr': 'اسم المادة (بالعربية)',
      'materials.brandName': 'اسم العلامة التجارية',
      'materials.mainUnit': 'الوحدة الرئيسية',
      'materials.quantityPerMainUnit': 'الكمية لكل وحدة رئيسية',
      'materials.quantityPerPiece': 'الكمية لكل قطعة',
      'materials.subUnit': 'الوحدة الفرعية',
      'materials.priceOfMainUnit': 'السعر بدون ضريبة القيمة المضافة',
      'materials.priceWithVat': 'السعر مع ضريبة القيمة المضافة',
      'materials.supplier': 'المورّد',
      'materials.category': 'التصنيف',
      'materials.unitCost': 'تكلفة الوحدة',
      'materials.includesVat': '(يشمل ضريبة القيمة المضافة)',
      'materials.excelUpload': 'استيراد/تصدير إكسل',
      'materials.excelImportExport': 'استيراد/تصدير إكسل',
      'materials.importExcelFile': 'استيراد ملف إكسل',
      'materials.importExcelDescription': 'استيراد بيانات المواد من إكسل',
      'materials.dropExcelFile': 'اسقط ملف إكسل هنا',
      'materials.releaseToImport': 'أفلت لاستيراد ملف إكسل',
      'materials.dragAndDropDescription': 'اسحب وأفلت ملف إكسل هنا، أو انقر للتصفح',
      'materials.exportMaterials': 'تصدير المواد',
      'materials.exporting': 'جاري التصدير...',
      'materials.downloadTemplate': 'تحميل القالب',
      'materials.downloadTemplateDescription': 'احصل على قالب إكسل بالتنسيق المطلوب',
      'materials.allergensColumn': 'عمود مسببات الحساسية',
      'materials.allergensColumnDescription': 'أدخل قيم مسببات الحساسية مفصولة بفواصل. اتركها فارغة إذا لم تكن هناك مسببات حساسية.',
      'materials.validAllergenValues': 'القيم الصحيحة:',
      'materials.allergenExamples': 'أمثلة:',
      'materials.importFile': 'استيراد ملف',
      'materials.importInProgress': 'جاري الاستيراد...',
      'materials.preview': 'معاينة',
      'materials.previewDescription': 'راجع بياناتك قبل الاستيراد',
      'materials.readyToImport': 'جاهز للاستيراد',
      'materials.removeFile': 'إزالة الملف',
      'materials.newCategories': 'تصنيفات جديدة',
      'materials.existingCategories': 'تصنيفات موجودة',
      'materials.unnamedMaterials': 'مواد غير مسماة',
      'materials.vatCalculations': 'حسابات ضريبة القيمة المضافة',
      'materials.vatCalculationMismatch': 'عدم تطابق في حساب ضريبة القيمة المضافة — تم التصحيح تلقائياً',
      'materials.unnamed': 'غير مسمى',
      'materials.brand': 'العلامة التجارية',
      'materials.mainUnit': 'الوحدة الرئيسية',
      'materials.qtyMain': 'الكمية/الرئيسية',
      'materials.qtyPiece': 'الكمية/القطعة',
      'materials.subUnit': 'الوحدة الفرعية',
      'materials.priceWithVat': 'السعر مع ضريبة القيمة المضافة',
      'materials.priceWithoutVat': 'السعر بدون ضريبة القيمة المضافة',
      'materials.calories': 'السعرات',
      'materials.serving': 'الحصة',
      'materials.allergens': 'مسببات الحساسية',
      'materials.uploadFile': 'رفع ملف',
      'materials.importingMaterials': 'جاري استيراد المواد',
      'materials.importingMaterialsDescription': 'يرجى الانتظار بينما نقوم بمعالجة وحفظ المواد...',
      'materials.successfullyUploaded': 'تم رفع {{count}} مادة بنجاح!',
      'materials.caloriesWholeNumbers': 'ستُعرض السعرات كأرقام صحيحة',
      'materials.emptyCategoriesUnnamed': 'سيتم تعيين التصنيفات الفارغة إلى "غير مسماة"',
      'materials.allergensCommaSeparated': 'مسببات الحساسية: قيم مفصولة بفواصل (قمح، حليب، بيض، إلخ)',
      
      // CategoryRecipes
      'recipes.directMaterialCosts': 'إدارة الأطباق والوصفات',
      'recipes.recipesInCategory': 'أطباق ووصفات في تصنيف {{category}}',
      'recipes.addNewRecipe': 'إضافة طبق جديد',
      'recipes.backToCategories': 'العودة إلى التصنيفات',
      'recipes.loading': 'جاري التحميل...',
      'recipes.selectCategory': 'اختر التصنيف',
      'recipes.searchByRecipeNameOrId': 'البحث بالاسم أو الرقم',
      'recipes.sortById': 'ترتيب حسب الرقم',
      'recipes.sortByName': 'ترتيب حسب الاسم',
      'recipes.recipes': 'الأطباق',
      'recipes.recipesTotal': '{{count}} طبق إجمالاً',
      'recipes.noRecipesFound': 'لم يتم العثور على أطباق',
      'recipes.noRecipesInCategory': 'لا توجد أطباق في هذا التصنيف',
      'recipes.noDishesAddedYet': 'لم يتم إضافة أي أطباق بعد.',
      'recipes.tryAdjustingSearchTerms': 'حاول تعديل مصطلحات البحث',
      'recipes.addFirstRecipe': 'أضف وصفتك الأولى للبدء',
      'recipes.addFirstRecipeButton': 'إضافة أول وصفة',
      'recipes.servings': 'حصص',
      'recipes.totalCost': 'التكلفة الإجمالية',
      'recipes.totalCostPerServing': 'التكلفة الإجمالية لكل حصة',
      'recipes.edit': 'تعديل',
      'recipes.delete': 'حذف',
      'recipes.duplicate': 'نسخ',
      'recipes.recipeDuplicatedSuccessfully': 'تم نسخ الوصفة بنجاح!',
      'recipes.failedToDuplicateRecipe': 'فشل في نسخ الوصفة',
      'recipes.recipeUpdatedSuccessfully': 'تم تحديث الطبق بنجاح! تم إعادة حساب جميع الأطباق التابعة.',
      'recipes.recipeCreatedSuccessfully': 'تم إنشاء الوصفة بنجاح!',
      'recipes.recipeDeletedSuccessfully': 'تم حذف الوصفة بنجاح!',
      'recipes.allDependentRecipesRecalculated': 'تم إعادة حساب جميع الوصفات التابعة.',
      'recipes.failedToLoadRecipesData': 'فشل في تحميل بيانات الأطباق',
      'recipes.failedToSaveRecipe': 'فشل في حفظ الوصفة',
      'recipes.errorRecipeCannotIncludeSelf': 'لا يمكن أن تتضمن الوصفة نفسها كمكون.',
      'recipes.errorCircularRecipeDependency':
        'لا يمكن إضافة هذه الوصفة كمكون: ذلك يخلق تبعية دائرية بين الوصفات.',
      'recipes.failedToDeleteRecipe': 'فشل في حذف الوصفة',
      'recipes.confirmAction': 'تأكيد الإجراء',
      'recipes.confirmDeleteRecipe': 'هل أنت متأكد من حذف هذه الوصفة؟',
      'recipes.cannotDeleteRecipeUsedAsIngredient': 'لا يمكن حذف وصفة مستخدمة كمكون في وصفات أخرى',
      
      // RecipeForm
      'recipeForm.recipeDetails': 'تفاصيل الطبق',
      'recipeForm.basicInformation': 'المعلومات الأساسية عن طبقك',
      'recipeForm.enterDishNameEnglish': 'أدخل اسم الطبق بالإنجليزية',
      'recipeForm.enterDishNameArabic': 'أدخل اسم الطبق بالعربية',
      'recipeForm.selectCategory': 'اختر التصنيف',
      'recipeForm.portionUnit': 'وحدة الحصة',
      'recipeForm.unit': 'الوحدة',
      'recipeForm.recipeImage': 'صورة الوصفة',
      'recipeForm.chooseImage': 'اختر صورة',
      'recipeForm.uploading': 'جاري الرفع...',
      'recipeForm.ingredients': 'المكونات',
      'recipeForm.directMaterialsLibrary': 'مكتبة المواد المباشرة',
      'recipeForm.showDirectMaterialsLibrary': 'إظهار مكتبة المواد المباشرة',
      'recipeForm.hideDirectMaterialsLibrary': 'إخفاء مكتبة المواد المباشرة',
      'recipeForm.searchByRecipeNameOrId': 'البحث بالاسم أو الرقم',
      'recipeForm.searchByMaterialNameOrId': 'البحث بالاسم أو الرقم',
      'recipeForm.searchByCategoryNameOrId': 'البحث بالاسم أو الرقم',
      'recipeForm.tip': 'نصيحة:',
      'recipeForm.clickSelectToAdd': 'انقر على "اختيار" على المكونات من المكتبة لإضافتها إلى وصفتك',
      'recipeForm.clickSelectToAddDescription': 'انقر على "اختيار" على المكونات من المكتبة أعلاه لإضافتها إلى وصفتك',
      'recipeForm.selectIngredient': 'اختر المكون',
      'recipeForm.selected': 'محدد',
      'recipeForm.material': 'مادة',
      'recipeForm.recipe': 'وصفة',
      'recipeForm.quantity': 'الكمية',
      'recipeForm.addFirstIngredient': 'إضافة أول مكون',
      'recipeForm.procedure': 'الإجراء',
      'recipeForm.step': 'الخطوة',
      'recipeForm.instruction': 'التعليمات',
      'recipeForm.enterStepInstruction': 'أدخل تعليمات الخطوة...',
      'recipeForm.stepImage': 'صورة الخطوة',
      'recipeForm.viewFullImage': 'عرض الصورة كاملة',
      'recipeForm.removeImage': 'إزالة الصورة',
      'recipeForm.uploadImage': 'رفع صورة',
      'recipeForm.dragToReorder': 'اسحب لإعادة الترتيب',
      'recipeForm.editQuantity': 'تعديل الكمية',
      'recipeForm.nutrition': 'التغذية',
      'recipeForm.calories': 'السعرات',
      'recipeForm.protein': 'البروتين',
      'recipeForm.carbs': 'الكربوهيدرات',
      'recipeForm.fat': 'الدهون',
      'recipeForm.fiber': 'الألياف',
      'recipeForm.sugar': 'السكر',
      'recipeForm.sodium': 'الصوديوم',
      'recipeForm.cholesterol': 'الكوليسترول',
      'recipeForm.allergens': 'مسببات الحساسية',
      'recipeForm.save': 'حفظ',
      'recipeForm.cancel': 'إلغاء',
      'recipeForm.add': 'إضافة',
      'recipeForm.remove': 'إزالة',
      'recipeForm.edit': 'تعديل',
      'recipeForm.delete': 'حذف',
      'recipeForm.upload': 'رفع',
      'recipeForm.choose': 'اختيار',
      'recipeForm.select': 'اختيار',
      'recipeForm.search': 'بحث',
      'recipeForm.error': 'خطأ',
      'recipeForm.warning': 'تحذير',
      'recipeForm.success': 'تم بنجاح',
      'recipeForm.pleaseEnterInstruction': 'يرجى إدخال تعليمات',
      'recipeForm.failedToLoadData': 'فشل في تحميل بيانات المواد والوصفات',
      'recipeForm.ingredient': 'مكون',
      'recipeForm.pleaseSelectImageFile': 'يرجى اختيار ملف صورة',
      'recipeForm.uploadCancelledByUser': 'تم إلغاء الرفع من قبل المستخدم',
      'recipeForm.atLeastOneIngredientRequired': 'مكون واحد على الأقل مطلوب للطبق الجديد',
      'recipeForm.allStepsMustHaveEnglishInstructions': 'جميع خطوات الإجراء يجب أن تحتوي على تعليمات إنجليزية',
      'recipeForm.recipeFormSubmitTriggered': 'تم تشغيل إرسال نموذج الوصفة',
      'recipeForm.recipeFormValidationFailed': 'فشل التحقق من نموذج الوصفة',
      'recipeForm.recipeFormValidationPassed': 'نجح التحقق من نموذج الوصفة',
      'recipeForm.newStepPreview': 'معاينة الخطوة الجديدة',
      'recipeForm.recipePreview': 'معاينة الوصفة',
      'recipeForm.stepPreview': 'معاينة الخطوة',
      'recipeForm.stepImage': 'صورة الخطوة',
      'recipeForm.holdingTemperature': 'درجة حرارة التخزين (°C)',
      'recipeForm.hasPricing': 'يحتوي على تسعير',
      'recipeForm.enablePricing': 'تفعيل التسعير',
      'recipeForm.enablePricingDescription': 'سيظهر هذا الطبق في صفحة التسعير لتعيين أسعار البيع وهوامش الربح.',
      'recipeForm.disablePricingDescription': 'سيتم استخدام هذا الطبق كمكون في أطباق أخرى فقط ولن يظهر في حسابات التسعير.',
      
      // Recipe Image Section
      'recipeForm.recipeImage': 'صورة الطبق',
      'recipeForm.addVisualRepresentation': 'أضف تمثيلاً بصرياً لطبقك',
      'recipeForm.recipePreview': 'معاينة الطبق',
      'recipeForm.uploaded': 'تم الرفع',
      'recipeForm.currentImage': 'الصورة الحالية',
      'recipeForm.imageUploadedSuccessfully': 'تم رفع صورة طبقك بنجاح.',
      'recipeForm.replaceImageDescription': 'يمكنك استبدال هذه الصورة برفع صورة جديدة أدناه.',
      'recipeForm.chooseImage': 'اختر صورة',
      'recipeForm.uploading': 'جاري الرفع...',
      'recipeForm.cancel': 'إلغاء',
      'recipeForm.imageRequirements': 'متطلبات الصورة',
      'recipeForm.formats': 'الصيغ',
      'recipeForm.maximumSize': 'الحد الأقصى للحجم',
      'recipeForm.recommended': 'مُوصى به',
      'recipeForm.quality': 'الجودة',
      'recipeForm.squareImagesRatio': 'صور مربعة (نسبة 1:1) للحصول على أفضل عرض',
      'recipeForm.highResolutionImages': 'الصور عالية الدقة تعمل بشكل أفضل',
      
      // Ingredients Tab
      'recipeForm.ingredientsLibrary': 'مكتبة المكونات',
      'recipeForm.showDirectMaterialsLibrary': 'إظهار مكتبة المواد المباشرة',
      'recipeForm.hideDirectMaterialsLibrary': 'إخفاء مكتبة المواد المباشرة',
      'recipeForm.allCategories': 'جميع التصنيفات',
      'recipeForm.allMaterials': 'جميع المواد',
      'recipeForm.allDishes': 'جميع الأطباق',
      'recipeForm.searchByDishNameOrId': 'البحث باسم الطبق أو المعرف',
      'recipeForm.searchByMaterialNameOrId': 'البحث باسم المادة أو المعرف',
      'recipeForm.searchByCategoryNameOrId': 'البحث باسم التصنيف أو المعرف',
      'recipeForm.select': 'اختر',
      'recipeForm.materials': 'المواد',
      'recipeForm.noDishesInCategory': 'لا توجد أطباق في هذا التصنيف',
      'recipeForm.tipClickSelect': '💡 نصيحة: انقر على "اختر" على المكونات من المكتبة لإضافتها إلى طبقك',
      'recipeForm.selectedIngredients': 'المكونات المختارة',
      'recipeForm.totalCost': 'التكلفة الإجمالية',
      'recipeForm.ingredient': 'مكون',
      'recipeForm.ingredients': 'مكونات',
      'recipeForm.piece': 'قطعة',
      'recipeForm.waste': 'هدر',
      'recipeForm.loadingAllergens': 'جاري تحميل مسببات الحساسية...',
      'recipeForm.noIngredientsAddedYet': 'لم تتم إضافة مكونات بعد',
      'recipeForm.clickSelectToAddIngredients': 'انقر على "اختر" على المكونات من المكتبة أعلاه لإضافتها إلى طبقك',
      'recipeForm.addAnIngredient': 'إضافة مكون',
      'recipeForm.materialsDishes': 'المواد/الأطباق',
      'recipeForm.selectIngredient': 'اختر مكون',
      'recipeForm.selected': 'مختار',
      'recipeForm.quantity': 'الكمية',
      'recipeForm.enterQuantity': 'أدخل الكمية',
      'recipeForm.wastePercentage': 'نسبة الهدر',
      'recipeForm.enterWastePercentage': 'أدخل نسبة الهدر',
      'recipeForm.add': 'إضافة',
      'recipeForm.addFirstIngredient': 'أضف مكونك الأول',
      'recipeForm.addFirstIngredientDescription': 'ابدأ في بناء طبقك بإضافة مكونات من المكتبة أعلاه',
      
      // Add Ingredient Modal
      'recipeForm.addIngredient': 'إضافة مكون',
      'recipeForm.specifyQuantityFor': 'حدد الكمية لـ',
      'recipeForm.quantity': 'الكمية',
      'recipeForm.quantityPlaceholder': 'مثال: 100',
      'recipeForm.wastePercentage': 'نسبة الهدر',
      'recipeForm.wastePercentageDescription': '(فقدان التحضير، فقدان الطبخ، إلخ)',
      'recipeForm.wastePercentagePlaceholder': 'مثال: 15',
      'recipeForm.youCanEditThisLater': 'يمكنك تعديل هذا لاحقاً.',
      'recipeForm.cancel': 'إلغاء',
      'recipeForm.add': 'إضافة',
      'recipeForm.editQuantity': 'تعديل الكمية',
      'recipeForm.updateQuantityFor': 'تحديث الكمية لـ',
      'recipeForm.update': 'تحديث',
      'recipeForm.duplicateIngredient': 'مكون مكرر',
      'recipeForm.ingredientAlreadyAdded': 'هذا المكون مضاف بالفعل.',
      'recipeForm.ok': 'موافق',
      
      // Procedure Tab
      'recipeForm.addProcedureSteps': 'إضافة خطوات الإجراء',
      'recipeForm.instruction': 'التعليمات',
      'recipeForm.stepImage': 'صورة الخطوة',
      'recipeForm.uploading': 'جاري الرفع...',
      'recipeForm.uploadImage': 'رفع صورة',
      'recipeForm.stepPreview': 'معاينة الخطوة',
      'recipeForm.imageSelected': 'تم اختيار الصورة',
      'recipeForm.removeImage': 'إزالة الصورة',
      'recipeForm.addStep': 'إضافة خطوة',
      'recipeForm.steps': 'الخطوات',
      'recipeForm.noProcedureStepsAddedYet': 'لم تتم إضافة خطوات إجراء بعد',
      'recipeForm.addStepsUsingFormAbove': 'أضف خطوات باستخدام النموذج أعلاه',
      'recipeForm.procedureError': 'خطأ في الإجراء',
      'recipeForm.stepImageModal': 'صورة الخطوة',
      
      // Pricing
      'pricing.title': 'تسعير الأطباق',
      'pricing.subtitle': 'إدارة تسعير أطباقك وتحليل الربحية',
      'pricing.searchPlaceholder': 'البحث باسم الطبق أو الرقم',
      'pricing.sortById': 'ترتيب حسب الرقم',
      'pricing.sortByName': 'ترتيب حسب الاسم',
      'pricing.allCategories': 'كل التصنيفات',
      'pricing.filterByCategory': 'تصفية حسب التصنيف',
      'pricing.categoriesSelected': '{{count}} تصنيفات',
      'pricing.dishes': 'الأطباق',
      'pricing.modifiedFromDefault': 'معدل من التكلفة الافتراضية لكل طبق',
      'pricing.noRecipesFound': 'لم يتم العثور على أطباق',
      'pricing.noRecipesAvailable': 'لا توجد أطباق متاحة',
      'pricing.tryAdjustingSearchTerms': 'حاول تعديل مصطلحات البحث',
      'pricing.createFirstRecipe': 'أنشئ طبقك الأول لبدء تحليل التسعير',
      'pricing.createRecipe': 'إنشاء طبق',
      'pricing.loadingRecipes': 'جاري تحميل الوصفات...',
      'pricing.id': 'الرقم',
      'pricing.dish': 'الطبق',
      'pricing.dishCostPerServing': 'تكلفة الطبق لكل حصة',
      'pricing.fixedCost': 'التكلفة الثابتة',
      'pricing.variableCost': 'التكلفة المتغيرة',
      'pricing.totalCost': 'التكلفة الإجمالية',
      'pricing.profitMargin': 'هامش الربح',
      'pricing.sellingPrice': 'سعر البيع',
      'pricing.marketPrice': 'سعر السوق',
      'pricing.pricingStatus': 'حالة التسعير',
      'pricing.actions': 'الإجراءات',
      'pricing.pricingSet': 'تم تعيين التسعير',
      'pricing.noPricing': 'لا يوجد تسعير',
      'pricing.editPricing': 'تعديل التسعير',
      'pricing.setPricing': 'تعيين التسعير',
      'pricing.fixed': 'ثابت',
      'pricing.variable': 'متغير',
      'pricing.total': 'إجمالي',
      'pricing.margin': 'هامش %',
      'pricing.profitCost': 'تكلفة الربح',
      'pricing.ofCost': '% من التكلفة',
      'pricing.vsSellingPrice': 'مقابل سعر البيع',
      'pricing.reset': 'إعادة تعيين',
      'pricing.save': 'حفظ',
      'pricing.cancel': 'إلغاء',
      'pricing.enterOneOfMarginOrPrice': 'يرجى إدخال واحد على الأقل من: هامش %، سعر البيع، تكلفة الربح، أو % من التكلفة',
      'pricing.messages.enterOneOfMarginOrPrice': 'يرجى إدخال واحد على الأقل من: هامش %، سعر البيع، تكلفة الربح، أو % من التكلفة',

      // Applications Pricing
      'applicationsPricing.title': 'تسعير التطبيقات',
      'applicationsPricing.subtitle': 'إدارة تطبيقات التسعير ونسب العلاوات المئوية للأطباق',
      'applicationsPricing.addApplication': 'إضافة تطبيق',
      'applicationsPricing.addNewPricingApplication': 'إضافة تطبيق تسعير جديد',
      'applicationsPricing.applicationName': 'اسم التطبيق',
      'applicationsPricing.applicationNamePlaceholder': 'مثال: هانجرستيشن، لوكيت، ذا شيفز',
      'applicationsPricing.percentageMarkup': 'نسبة الربح المئوية',
      'applicationsPricing.saving': 'جاري الحفظ...',
      'applicationsPricing.save': 'حفظ',
      'applicationsPricing.cancel': 'إلغاء',
      'applicationsPricing.applicationsList': 'قائمة التطبيقات',
      'applicationsPricing.searchByApplicationNameOrId': 'البحث باسم التطبيق أو الرقم',
      'applicationsPricing.sortById': 'ترتيب حسب الرقم',
      'applicationsPricing.sortByName': 'ترتيب حسب الاسم',
      'applicationsPricing.pricingApplications': 'تطبيقات التسعير',
      'applicationsPricing.noApplicationsFound': 'لم يتم العثور على تطبيقات',
      'applicationsPricing.noPricingApplicationsFound': 'لم يتم العثور على تطبيقات تسعير',
      'applicationsPricing.tryAdjustingSearchCriteria': 'حاول تعديل معايير البحث',
      'applicationsPricing.createFirstPricingApplication': 'أنشئ أول تطبيق تسعير لبدء إدارة نسب العلاوات المئوية',
      'applicationsPricing.percentage': 'نسبة العلاوة المئوية',
      'applicationsPricing.created': 'تم الإنشاء',
      'applicationsPricing.edit': 'تعديل',
      'applicationsPricing.delete': 'حذف',
      'applicationsPricing.active': 'نشط',
      'applicationsPricing.inactive': 'غير نشط',
      'applicationsPricing.dishPricing': 'تسعير الأطباق',
      'applicationsPricing.showingDishesWithPricing': 'عرض الأطباق مع التسعير الحالي والأسعار المحسوبة مع علاوة {{percentage}}%',
      'applicationsPricing.searchByDishNameOrId': 'البحث باسم الطبق أو الرقم',
      'applicationsPricing.sortByPrice': 'ترتيب حسب السعر ↑',
      'applicationsPricing.sortByPriceDesc': 'ترتيب حسب السعر ↓',
      'applicationsPricing.currentSellingPrice': 'سعر البيع الحالي',
      'applicationsPricing.applicationMarkup': 'علاوة التطبيق',
      'applicationsPricing.newSellingPrice': 'سعر البيع الجديد',
      'applicationsPricing.priceDifference': 'فرق السعر',
      'applicationsPricing.noDishesFoundMatchingSearch': 'لم يتم العثور على أطباق تطابق البحث',
      'applicationsPricing.noDishesWithPricingFound': 'لم يتم العثور على أطباق مع تسعير',
      'applicationsPricing.tryAdjustingSearchTerms': 'حاول تعديل مصطلحات البحث',
      'applicationsPricing.createRecipesAndSetPricing': 'أنشئ وصفات وحدد تسعيرها لرؤيتها هنا',
      'applicationsPricing.deletePricingApplication': 'حذف تطبيق التسعير',
      'applicationsPricing.confirmDeleteApplication': 'هل أنت متأكد من حذف تطبيق التسعير "{{name}}"؟',
      'applicationsPricing.deleting': 'جاري الحذف...',
      'applicationsPricing.deleteApplication': 'حذف التطبيق',
      'applicationsPricing.loadingPricingApplications': 'جاري تحميل تطبيقات التسعير...',
      'applicationsPricing.applicationNameRequired': 'اسم التطبيق مطلوب',
      'applicationsPricing.percentageMustBeBetween': 'يجب أن تكون النسبة بين 0 و 1000',
      'applicationsPricing.failedToCreatePricingApplication': 'فشل في إنشاء تطبيق التسعير',
      'applicationsPricing.failedToUpdatePricingApplication': 'فشل في تحديث تطبيق التسعير',
      'applicationsPricing.failedToToggleApplicationStatus': 'فشل في تبديل حالة التطبيق',
      'applicationsPricing.failedToDeletePricingApplication': 'فشل في حذف تطبيق التسعير',
      'applicationsPricing.failedToLoadData': 'فشل في تحميل البيانات',

      // Settings Modal
      'settings.title': 'الإعدادات',
      'settings.subtitle': 'إدارة إعدادات التطبيق',
      'settings.themeSettings': 'إعدادات المظهر',
      'settings.darkMode': 'الوضع المظلم',
      'settings.lightMode': 'الوضع المضيء',
      'settings.darkThemeActive': 'المظهر المظلم نشط حالياً',
      'settings.lightThemeActive': 'المظهر المضيء نشط حالياً',
      'settings.switchToLight': 'التبديل إلى المضيء',
      'settings.switchToDark': 'التبديل إلى المظلم',
      'settings.languageSettings': 'إعدادات اللغة',
      'settings.switchToArabic': 'التبديل إلى العربية',
      'settings.switchToEnglish': 'التبديل إلى الإنجليزية',
      'settings.currentLanguage': 'اللغة الحالية',

      // VAT Settings
      'vatSettings.title': 'إعدادات ضريبة القيمة المضافة',
      'vatSettings.vatPercentage': 'نسبة ضريبة القيمة المضافة',
      'vatSettings.edit': 'تعديل',
      'vatSettings.save': 'حفظ',
      'vatSettings.cancel': 'إلغاء',
      'vatSettings.vatPercentageMustBeBetween': 'يجب أن تكون نسبة ضريبة القيمة المضافة بين 0 و 100',
      'vatSettings.vatSettingsUpdatedSuccessfully': 'تم تحديث إعدادات ضريبة القيمة المضافة بنجاح! تم إعادة حساب جميع أسعار المواد.',
      'vatSettings.failedToUpdateVatSettings': 'فشل في تحديث إعدادات ضريبة القيمة المضافة',
      'vatSettings.changingVatPercentageWillRecalculate': 'تغيير نسبة ضريبة القيمة المضافة سيعيد حساب جميع أسعار المواد تلقائياً',
      'vatSettings.defaultVatRate': 'معدل ضريبة القيمة المضافة الافتراضي: 15%',
      'vatSettings.pricesDisplayedWithVatIncluded': 'الأسعار معروضة مع ضريبة القيمة المضافة',

      // Logo Settings
      'logoSettings.title': 'إعدادات الشعار',
      'logoSettings.uploadLogo': 'رفع الشعار',
      'logoSettings.currentLogo': 'الشعار الحالي',
      'logoSettings.logoPreview': 'معاينة الشعار (لم يتم الحفظ بعد)',
      'logoSettings.selectFile': 'اختر ملف',
      'logoSettings.uploading': 'جاري الرفع...',
      'logoSettings.save': 'حفظ',
      'logoSettings.saving': 'جاري الحفظ...',
      'logoSettings.cancel': 'إلغاء',
      'logoSettings.removeLogo': 'إزالة الشعار',
      'logoSettings.pleaseSelectImageFile': 'يرجى اختيار ملف صورة',
      'logoSettings.imageSizeLimit': 'يجب أن يكون حجم الصورة أقل من 2 ميجابايت',
      'logoSettings.logoUploadedSuccessfully': 'تم رفع الشعار بنجاح',
      'logoSettings.failedToUploadLogo': 'فشل في رفع الشعار',
      'logoSettings.pleaseUploadLogoFirst': 'يرجى رفع الشعار أولاً',
      'logoSettings.logoSavedSuccessfully': 'تم حفظ الشعار بنجاح',
      'logoSettings.failedToSaveLogo': 'فشل في حفظ الشعار',
      'logoSettings.logoRemovedSuccessfully': 'تم إزالة الشعار بنجاح',
      'logoSettings.failedToRemoveLogo': 'فشل في إزالة الشعار',
      'logoSettings.logoUploadDescription': 'قم برفع شعار شركتك أو علامتك التجارية (اختياري)',
      'logoSettings.recommendedSize': 'الحجم الموصى به: 200x200 بكسل أو أعلى',
      'logoSettings.supportedFormats': 'الصيغ المدعومة: PNG، JPG، JPEG، SVG',
      'logoSettings.failedToPreviewImage': 'فشل في معاينة الصورة',
      'logoSettings.noChangesToSave': 'لا توجد تغييرات للحفظ',
      'logoSettings.uploadCancelled': 'تم إلغاء الرفع',

      // Steps Section
      'recipeForm.viewFullImage': 'عرض الصورة كاملة',
      'recipeForm.removeImage': 'إزالة الصورة',
      'recipeForm.save': 'حفظ',
      'recipeForm.cancel': 'إلغاء',
      'recipeForm.edit': 'تعديل',
      'recipeForm.delete': 'حذف',
      'recipeForm.areYouSureDeleteStep': 'هل أنت متأكد من أنك تريد حذف هذه الخطوة؟',
      'recipeForm.backToIngredients': 'العودة إلى المكونات',
      'recipeForm.recipeOverview': 'نظرة عامة على الطبق',
      'recipeForm.backToRecipeOverview': 'العودة إلى نظرة عامة على الطبق',
      'recipeForm.nextProcedure': 'التالي: الإجراء',
      'recipeForm.nextIngredients': 'التالي: المكونات',
      'recipeForm.ingredients': 'المكونات',
      'recipeForm.procedure': 'الإجراء',
      'recipeForm.addAtLeastOneIngredientToCreateDish': 'أضف مكوناً واحداً على الأقل لإنشاء طبقك.',
      'recipeForm.useFormAboveToAddIngredients': 'استخدم النموذج أعلاه لإضافة المكونات.',
      'recipeForm.dragIngredientsFromLibraryToAddThem': 'اسحب المكونات من المكتبة لإضافتها.',
      
      // Recipes
      'recipes.title': 'إدارة الأطباق والوصفات',
      'recipes.subtitle': 'اختر تصنيف لعرض وإدارة أطباقه ووصفاته',
      'recipes.addNewCategory': 'إضافة تصنيف جديد',
      'recipes.addNewDish': 'إضافة طبق جديد',
      'recipes.editDish': 'تعديل الطبق',
      'recipes.recipeId': 'رقم الوصفة',
      'recipes.dishNameEn': 'اسم الطبق (بالإنجليزية)',
      'recipes.dishNameAr': 'اسم الطبق (بالعربية)',
      'recipes.dishImage': 'صورة الطبق',
      'recipes.numberOfServings': 'عدد الحصص',
      'recipes.servings': 'حصص',
      'recipes.portionSize': 'حجم الحصة الواحدة',
      'recipes.ingredients': 'المكونات',
      'recipes.addIngredient': 'إضافة مكون',
      'recipes.code': 'الرمز',
      'recipes.quantityUsed': 'الكمية المستخدمة',
      'recipes.type': 'النوع',
      'recipes.rawMaterial': 'مادة خام',
      'recipes.recipe': 'وصفة',
      'recipes.totalDishCost': 'التكلفة الإجمالية للطبق',
      'recipes.costPerServing': 'التكلفة الإجمالية لكل حصة',
      
      // Dashboard
      'dashboard.title': 'لوحة متابعة التكاليف',
      'dashboard.dishCosts': 'تكلفة الأطباق لكل حصة',
      'dashboard.ingredientBreakdown': 'تفصيل المواد المباشرة',
      'dashboard.noMaterialsUsedInDishes': 'لا يتم استخدام أي مواد حالياً في الأطباق.',
      'dashboard.addMaterialsToSeeBreakdown': 'أضف مواداً إلى الأطباق لرؤية مخطط تفصيل التكلفة هنا.',
      'dashboard.costDistribution': 'توزيع التكلفة',
      'dashboard.dishComparison': 'مقارنة تكلفة الأطباق',
      'dashboard.noDishesForComparison': 'لا توجد أطباق متاحة حالياً للمقارنة.',
      'dashboard.addDishesToSeeComparison': 'أضف أطباقاً لرؤية مخطط مقارنة التكلفة هنا.',
      'dashboard.topMaterials': 'أكثر المواد استخداماً',
      'dashboard.materialUsageStats': 'إحصائيات استخدام المواد',
      'dashboard.topCostContributors': 'أكبر المساهمين في التكلفة',
      'dashboard.noMaterialsWithCostsFound': 'لم يتم العثور على مواد بتكاليف',
      'dashboard.usageDistribution': 'توزيع الاستخدام',
      'dashboard.costAnalysisAndInsights': 'تحليل التكاليف والرؤى',
      'dashboard.totalMaterials': 'إجمالي المواد',
      'dashboard.totalRecipes': 'إجمالي الأطباق',
      'dashboard.totalMonthlyFixedCosts': 'إجمالي التكاليف الثابتة الشهرية',
      'dashboard.totalMonthlyVariableCosts': 'إجمالي التكاليف المتغيرة الشهرية',
      'dashboard.breakEvenUnits': 'وحدات نقطة التعادل',
      'dashboard.breakEvenUnitsDescription': 'عدد الأطباق لتحقيق نقطة التعادل',
      'dashboard.breakEvenSales': 'مبيعات نقطة التعادل',
      'dashboard.breakEvenSalesDescription': 'قيمة المبيعات لتحقيق نقطة التعادل',
      'dashboard.breakEvenEqualDistributionNote': 'يتم توزيع الطلبات المتوقعة بالتساوي على جميع الأطباق في كل فئة.',
      'dashboard.dishes': 'أطباق',
      'dashboard.units': 'وحدة',
      'dashboard.totalDishes': 'إجمالي الأطباق',
      'dashboard.usedInDishes': 'مستخدم في الأطباق',
      'dashboard.highUsageDishes': 'استخدام عالي (3+ أطباق)',
      'dashboard.mediumUsageDishes': 'استخدام متوسط (1-2 طبق)',
      'dashboard.totalCostInDishes': 'التكلفة الإجمالية في الأطباق',
      'dashboard.unusedMaterials': 'مواد غير مستخدمة',
      'dashboard.avgCostPerMaterial': 'متوسط تكلفة الوحدة لجميع المواد',
      'dashboard.totalFixedCosts': 'إجمالي التكاليف الثابتة الشهرية',
      'dashboard.totalFixedAndVariableCosts': 'إجمالي التكاليف الثابتة والمتغيرة',
      'dashboard.averageContributionMargin': 'متوسط ​​هامش المساهمة',
      'dashboard.breakEvenExcludedOrdersNote': '(تم استبعاد {{count}} طلبًا متوقعًا من فئات لا تحتوي على أطباق مسعّرة.)',
      'dashboard.breakEvenExcludedFixedCostsNote': '(تم استبعاد {{amount}} من التكاليف الثابتة الشهرية لفئات لا تحتوي على أطباق مسعّرة.)',
      'dashboard.breakEvenPartialPricingWarning': '{{category}}: {{priced}} من {{total}} أطباق مسعّرة؛ يتم توزيع الطلبات على جميع الأطباق في هذه الفئة.',
      'dashboard.totalExpectedOrders': 'إجمالي الطلبات المتوقعة',
      'dashboard.weightedAverageSellingPrice': 'متوسط سعر البيع المرجح',
      'dashboard.totalContribution': 'إجمالي المساهمة',
      
      // Categories
      'categories.title': 'التصنيفات',
      'categories.addNew': 'إضافة تصنيف جديد',
      'categories.editCategory': 'تعديل التصنيف',
      'categories.nameEn': 'اسم التصنيف (بالإنجليزية)',
      'categories.nameAr': 'اسم التصنيف (بالعربية)',
      'categories.image': 'صورة التصنيف',
      
      // Validation
      'validation.required': 'هذا الحقل مطلوب',
      'validation.invalidNumber': 'يرجى إدخال رقم صحيح',
      'validation.duplicateIngredient': 'هذا المكون مضاف بالفعل',
      'validation.materialInUse': 'لا يمكن حذف مادة مستخدمة في الوصفات',
      'validation.recipeInUse': 'لا يمكن حذف وصفة مستخدمة كمكون في وصفات أخرى',
      
      // Messages
      'messages.materialSaved': 'تم حفظ المادة بنجاح',
      'messages.materialDeleted': 'تم حذف المادة بنجاح',
      'messages.bulkMaterialsDeleted': 'تم حذف المواد بنجاح (العدد: {{count}}).',
      'messages.recipeSaved': 'تم حفظ الوصفة بنجاح',
      'messages.recipeDeleted': 'تم حذف الوصفة بنجاح',
      'messages.bulkRecipesDeleted': 'تم حذف الوصفات بنجاح (العدد: {{count}}).',
      'messages.categorySaved': 'تم حفظ التصنيف بنجاح',
      'messages.categoryDeleted': 'تم حذف التصنيف بنجاح',
      'messages.bulkCategoriesDeleted': 'تم حذف التصنيفات بنجاح (العدد: {{count}}).',
      'messages.bulkMaterialsDeletedPartial': 'تم حذف بعض المواد بنجاح (العدد: {{count}}).',
      'messages.bulkMaterialsBlockedInRecipes': 'تعذر حذف مواد لأنها مستخدمة في الوصفات (العدد: {{count}}).',
      'messages.bulkMaterialsOtherErrors': 'فشل حذف بعض المواد بسبب أخطاء أخرى (العدد: {{count}}).',
      'messages.bulkMaterialsDeleteFailedPrefix': 'فشل حذف المواد:',
      'messages.bulkMaterialsInRecipes': 'توجد مواد مستخدمة في الوصفات (العدد: {{count}}).',
      'messages.bulkMaterialsOtherFailed': 'تعذر حذف بعض المواد بسبب أخطاء أخرى (العدد: {{count}}).',
      'messages.bulkCategoriesDeletedPartial': 'تم حذف بعض التصنيفات بنجاح (العدد: {{count}}).',
      'messages.bulkCategoriesContainMaterials': 'تعذر حذف تصنيفات لأنها تحتوي على مواد (العدد: {{count}}).',
      'messages.bulkCategoriesOtherErrors': 'فشل حذف بعض التصنيفات بسبب أخطاء أخرى (العدد: {{count}}).',
      'messages.bulkCategoriesDeleteFailedPrefix': 'فشل حذف التصنيفات:',
      'messages.bulkCategoriesContainMaterialsShort': 'توجد تصنيفات تحتوي على مواد (العدد: {{count}}).',
      'messages.bulkCategoriesOtherFailed': 'تعذر حذف بعض التصنيفات بسبب أخطاء أخرى (العدد: {{count}}).',
      'messages.bulkDeleteUnexpected': 'حدث خطأ غير متوقع أثناء الحذف المتعدد. يرجى المحاولة مرة أخرى.',
      'messages.cannotDeleteCategoryContainsMaterials': 'لا يمكن حذف التصنيف "{{name}}" لأنه يحتوي على {{count}} مادة. يرجى حذف جميع المواد في هذا التصنيف أولاً ثم المحاولة مرة أخرى.',
      'messages.cannotDeleteCategoryUsedInMaterials': 'لا يمكن حذف التصنيف لأنه مستخدم في المواد. يرجى إزالة جميع المواد من هذا التصنيف أولاً.',
      'messages.cannotDeleteMaterialUsedInRecipes': 'لا يمكن حذف المادة لأنها مستخدمة في الوصفات. يرجى إزالتها من جميع الوصفات أولاً.',
      'messages.cannotDeleteMaterialUsedInRecipesWithCount': 'لا يمكن حذف المادة لأنها مستخدمة في الوصفات. هذه المادة مستخدمة في {{count}} وصفة.',
      'messages.failedDeleteMaterial': 'فشل حذف المادة',
      'messages.failedDeleteCategory': 'فشل حذف التصنيف',
      'messages.cannotDeleteCategoryContainsRecipes': 'لا يمكن حذف التصنيف "{{name}}" لأنه يحتوي على {{count}} وصفة. يرجى حذف جميع الوصفات في هذا التصنيف أولاً ثم المحاولة مرة أخرى.',
      'messages.cannotDeleteCategoryUsedInRecipes': 'لا يمكن حذف التصنيف لأنه مستخدم في الوصفات. يرجى إزالة جميع الوصفات من هذا التصنيف أولاً.',
      'messages.failedDeleteRecipe': 'فشل حذف الوصفة',
      'messages.failedUploadMaterials': 'فشل رفع المواد',
      'messages.failedUploadMaterialsWithReason': 'فشل رفع المواد: {{reason}}',
      'messages.failedCreateMaterial': 'فشل إنشاء المادة',
      'messages.excelUploaded': 'تم رفع ملف الإكسل بنجاح',
      'messages.error': 'حدث خطأ',

      // Authentication
      'auth.signIn': 'تسجيل الدخول',
      'auth.signUp': 'إنشاء حساب',
      'auth.signOut': 'تسجيل الخروج',
      'auth.createAccount': 'إنشاء حساب',
      'auth.haveAccount': 'لديك حساب بالفعل؟',
      'auth.noAccount': 'ليس لديك حساب؟',
      'auth.email': 'البريد الإلكتروني',
      'auth.username': 'اسم المستخدم',
      'auth.password': 'كلمة المرور',
      'auth.confirmPassword': 'تأكيد كلمة المرور',
      'auth.emailPlaceholder': 'أدخل بريدك الإلكتروني',
      'auth.usernamePlaceholder': 'أدخل اسم المستخدم',
      'auth.passwordPlaceholder': 'أدخل كلمة المرور',
      'auth.confirmPasswordPlaceholder': 'أكد كلمة المرور',
      'auth.phoneNumber': 'رقم الهاتف',
      'auth.phoneNumberPlaceholder': 'أدخل رقم هاتفك',
      'auth.country': 'البلد',
      'auth.countryPlaceholder': 'اختر بلدك',
      'auth.referralCode': 'رمز الإحالة',
      'auth.referralCodePlaceholder': 'أدخل رمز الإحالة (اختياري)',
      'auth.referralCodeHint': 'سيتم تطبيق رمز الإحالة على حسابك',
      'auth.optional': 'اختياري',

      // الشروط والأحكام
      'terms.agreeLabel': 'لقد قرأت ووافقت على',
      'terms.linkText': 'الشروط والأحكام',
      'terms.close': 'إغلاق',
      'terms.mustAgree': 'يجب الموافقة على الشروط والأحكام لإنشاء حساب',
      'terms.lastUpdatedLabel': 'آخر تحديث',
      'terms.lastUpdatedValue': 'مايو 2026',
      'terms.userTitle': 'الشروط والأحكام',
      'terms.userBody': [
        {
          heading: '1. قبول الشروط',
          body: 'بإنشائك حساباً أو استخدامك لهذا التطبيق، فإنك تؤكد أنك قد قرأت وفهمت ووافقت على الالتزام بهذه الشروط والأحكام وسياسة الخصوصية الخاصة بنا. إذا كنت لا توافق، فيرجى عدم إنشاء حساب أو استخدام الخدمة.'
        },
        {
          heading: '2. الحساب والأهلية',
          body: 'يجب أن لا يقل عمرك عن 18 عاماً لإنشاء حساب. توافق على تقديم معلومات دقيقة وحديثة وكاملة عند التسجيل والحفاظ على سرية بيانات حسابك. أنت مسؤول عن جميع الأنشطة التي تتم من خلال حسابك. يُسمح بحساب واحد لكل شخص.'
        },
        {
          heading: '3. الاشتراك والدفع',
          body: 'يتطلب الوصول إلى الميزات المدفوعة اشتراكاً سنوياً نشطاً. تتم محاسبة الخطط مقدماً لفترة الاشتراك السنوية. جميع الرسوم غير قابلة للاسترداد إلا حيث يقتضي القانون المعمول به ذلك. يظل اشتراكك نشطاً خلال الفترة المدفوعة ويمكن تجديده بإكمال دفعة جديدة. أنت مسؤول عن أي ضرائب مطبقة.'
        },
        {
          heading: '4. الاستخدام المقبول',
          body: 'توافق على عدم: (أ) استخدام الخدمة لأي غرض غير قانوني؛ (ب) محاولة الهندسة العكسية أو فك التشفير أو استخراج الكود المصدري؛ (ج) كشط أو نسخ أو إعادة بيع الوصول إلى التطبيق؛ (د) مشاركة بيانات حسابك مع أطراف ثالثة؛ أو (هـ) التدخل في أمن أو التشغيل العادي للخدمة.'
        },
        {
          heading: '5. البيانات والخصوصية',
          body: 'تحتفظ بملكية الوصفات والمواد والأسعار وغيرها من البيانات التي تدخلها في التطبيق. نقوم بتخزين ومعالجة هذه البيانات فقط لتقديم وتحسين الخدمة، وفقاً لسياسة الخصوصية الخاصة بنا. لن نبيع بيانات عملك لأطراف ثالثة.'
        },
        {
          heading: '6. الملكية الفكرية',
          body: 'التطبيق وواجهة المستخدم والكود المصدري والتصاميم والشعارات والوثائق مملوكة لنا ومحمية بموجب قوانين الملكية الفكرية. تظل بيانات الإدخال الخاصة بك ملكاً لك. لا يجوز نسخ أو تعديل أو إعادة توزيع أي جزء من التطبيق دون إذن كتابي منا.'
        },
        {
          heading: '7. توافر الخدمة والتغييرات',
          body: 'يتم توفير الخدمة "كما هي" و"حسب التوفر". نحن لا نضمن التشغيل دون انقطاع أو خالٍ من الأخطاء. قد نقوم بإضافة ميزات أو تعديلها أو إزالتها، وقد نوقف الخدمة مؤقتاً للصيانة مع إشعار معقول حيثما كان ذلك ممكناً.'
        },
        {
          heading: '8. تحديد المسؤولية',
          body: 'إلى أقصى حد يسمح به القانون، نحن غير مسؤولين عن أي أضرار غير مباشرة أو عرضية أو خاصة أو تبعية أو عقابية، بما في ذلك فقدان الأرباح أو الإيرادات أو البيانات أو فرص العمل. لن تتجاوز مسؤوليتنا الإجمالية المبلغ الذي دفعته لنا مقابل آخر اشتراك سنوي نشط.'
        },
        {
          heading: '9. الإنهاء',
          body: 'يمكنك إلغاء اشتراكك والتوقف عن استخدام الخدمة في أي وقت من إعدادات حسابك. قد نقوم بتعليق أو إنهاء الحسابات التي تنتهك هذه الشروط أو تشارك في نشاط احتيالي أو مسيء أو غير قانوني، بإشعار مسبق أو بدونه.'
        },
        {
          heading: '10. القانون الحاكم والاتصال',
          body: 'تخضع هذه الشروط لقوانين المملكة العربية السعودية. تُحل أي نزاعات في محاكم المملكة العربية السعودية. للأسئلة أو الإشعارات المتعلقة بهذه الشروط، يرجى الاتصال بفريق الدعم لدينا من خلال التطبيق.'
        }
      ],
      'terms.affiliateTitle': 'شروط وأحكام برنامج الشركاء',
      'terms.affiliateBody': [
        {
          heading: '1. القبول والأهلية',
          body: 'بانضمامك إلى برنامج الشركاء، فإنك تؤكد أنك قد قرأت ووافقت على هذه الشروط. يجب أن لا يقل عمرك عن 18 عاماً، وتقديم معلومات هوية ودفع دقيقة، ويجب ألا تكون موظفاً حالياً أو متعاقداً أو فرداً من العائلة المباشرة لموظف في هذه الشركة.'
        },
        {
          heading: '2. آلية الإحالة',
          body: 'ستحصل على رمز إحالة و/أو رابط فريد. تُحسب العمولات فقط على الاشتراكات المدفوعة المؤكدة للمستخدمين الجدد الذين يسجلون باستخدام رمز الإحالة أو الرابط الخاص بك ويكملون الدفع. الإحالات الذاتية وإحالات المستخدمين الحاليين غير مؤهلة.'
        },
        {
          heading: '3. معدل العمولة والتتبع',
          body: 'معدل العمولة وأي هيكل متدرج هي كما هو منشور على لوحة الشركاء الخاصة بك وقد يتم تحديثها من وقت لآخر بإشعار مسبق. يتم تتبع الإحالات عبر إسناد رمز الإحالة عند التسجيل؛ نحن غير مسؤولين عن إخفاقات التتبع الناجمة عن إجراءات المستخدم أو حاجبي الإعلانات أو أدوات الطرف الثالث.'
        },
        {
          heading: '4. شروط الدفع',
          body: 'تصبح العمولات مستحقة الدفع بمجرد أن يصل رصيدك المؤكد إلى الحد الأدنى لعتبة الدفع. تتم معالجة المدفوعات في دورة تسوية شهرية باستخدام طريقة الدفع التي اخترتها وقمت بالتحقق منها. أنت المسؤول الوحيد عن أي ضرائب أو رسوم أو التزامات امتثال مطبقة على مدفوعاتك.'
        },
        {
          heading: '5. الترويج المحظور',
          body: 'لا يجوز لك: (أ) إرسال رسائل جماعية غير مرغوب فيها (سبام)؛ (ب) المزايدة على الكلمات الرئيسية لعلامتنا التجارية في البحث المدفوع؛ (ج) استخدام حشو ملفات تعريف الارتباط أو النقرات القسرية أو التتبع الخادع؛ (د) الإحالة الذاتية أو استخدام حسابات وهمية؛ أو (هـ) تقديم ادعاءات مضللة أو غير مصرح بها حول التطبيق أو ميزاته أو نتائجه.'
        },
        {
          heading: '6. استخدام العلامة التجارية',
          body: 'نمنحك ترخيصاً محدوداً وقابلاً للإلغاء وغير حصري لاستخدام اسمنا وشعارنا وأصول التسويق المعتمدة فقط للترويج للتطبيق كجزء من هذا البرنامج. لا يجوز لك تعديل شعارنا أو التلميح إلى وظيفة أو موافقة رسمية أو تسجيل نطاقات أو حسابات وسائل تواصل تحتوي على علامتنا التجارية.'
        },
        {
          heading: '7. الإنهاء والاسترداد',
          body: 'يجوز لأي من الطرفين إنهاء علاقة الشريك في أي وقت بإشعار كتابي (داخل التطبيق أو البريد الإلكتروني). إذا أسأت استخدام النظام أو برنامج الشركاء، أو أنشأت إحالات وهمية أو مضللة، أو تلاعبت بالتتبع، أو خالفت هذه الشروط، أو شاركت في نشاط احتيالي أو ضار، فقد يتم تعليق أو إنهاء حساب الشريك الخاص بك فوراً، وقد تصبح غير مؤهل لاستلام أي عمولات معلقة أو غير مدفوعة. العمولات المكتسبة على الاشتراكات التي يتم استردادها لاحقاً أو رفضها أو إلغاؤها ضمن نافذة الاسترداد أو تحديدها كاحتيالية قد يتم عكسها وخصمها من المدفوعات المستقبلية أو المعلقة.'
        },
        {
          heading: '8. السرية',
          body: 'المعلومات غير العامة المتاحة من خلال لوحة الشركاء (بما في ذلك بيانات التحويل ومعدلات العمولة والميزات غير المنشورة) هي معلومات سرية. توافق على عدم الإفصاح عنها لأطراف ثالثة أو استخدامها خارج برنامج الشركاء.'
        },
        {
          heading: '9. تحديد المسؤولية والتعويض',
          body: 'توافق على تعويضنا وإبراء ذمتنا من أي مطالبات أو خسائر أو أضرار تنشأ عن خرقك لهذه الشروط أو أنشطتك الترويجية. مسؤوليتنا الإجمالية تجاهك محددة بالعمولات المدفوعة لك عن آخر دورة دفع مكتملة.'
        },
        {
          heading: '10. القانون الحاكم والاتصال',
          body: 'تخضع شروط الشركاء هذه لقوانين المملكة العربية السعودية. تُحل أي نزاعات في محاكم المملكة العربية السعودية. للأسئلة المتعلقة بالشركاء، يرجى الاتصال بفريق دعم الشركاء لدينا من خلال لوحة الشركاء.'
        }
      ],

      'auth.signInDescription': 'سجل دخولك إلى حسابك',
      'auth.createAccountDescription': 'إنشاء حساب جديد',
      'auth.loginFailed': 'فشل تسجيل الدخول. يرجى التحقق من بياناتك.',
      'auth.registrationFailed': 'فشل إنشاء الحساب. يرجى المحاولة مرة أخرى.',
      'auth.logoutSuccess': 'تم تسجيل الخروج بنجاح',
      'auth.verifyingEmail': 'جاري التحقق من بريدك الإلكتروني...',
      'auth.emailVerified': 'تم التحقق من البريد الإلكتروني!',
      'auth.redirectingToDashboard': 'سيتم توجيهك إلى لوحة التحكم قريباً...',
      'auth.goToDashboard': 'الذهاب إلى لوحة التحكم',
      'auth.goToLogin': 'الذهاب لتسجيل الدخول',
      'auth.emailVerification': 'التحقق من البريد الإلكتروني',
      'auth.checkEmailForVerification': 'يرجى التحقق من بريدك الإلكتروني للحصول على رابط التحقق. إذا لم تستلم البريد الإلكتروني، يمكنك طلب واحد جديد.',
      'auth.didntReceiveEmail': 'لم تستلم البريد الإلكتروني؟',
      'auth.enterUserIdToResend': 'أدخل معرف المستخدم لإعادة إرسال بريد التحقق:',
      'auth.enterUserId': 'أدخل معرف المستخدم',
      'auth.sending': 'جاري الإرسال...',
      'auth.resendVerificationEmail': 'إعادة إرسال بريد التحقق',
      'auth.backToLogin': 'العودة إلى تسجيل الدخول',
      'auth.userIdRequired': 'معرف المستخدم مطلوب لإعادة إرسال بريد التحقق',
      'auth.unexpectedError': 'حدث خطأ غير متوقع',
      'auth.checkYourEmail': 'تحقق من بريدك الإلكتروني',
      'auth.verificationLinkSent': 'لقد أرسلنا رابط التحقق إلى {{email}}. يرجى التحقق من بريدك الإلكتروني والنقر على رابط التحقق لتفعيل حسابك.',
      'auth.emailAlreadyVerified': 'تم التحقق من بريدك الإلكتروني مسبقًا.',
      'auth.verificationTokenAlreadyUsed': 'تم استخدام رابط التحقق هذا من قبل.',
      'auth.checkEmailForActivation': 'يرجى التحقق من بريدك الإلكتروني للحصول على رابط التحقق لتفعيل حسابك.',
      'auth.checkSpamFolder': 'تحقق من مجلد الرسائل غير المرغوب فيها أو انقر على الزر أدناه لإعادة إرسال بريد التحقق.',
      'auth.userIdNotAvailable': 'معرف المستخدم غير متوفر',
      'auth.emailNotVerified': 'يرجى التحقق من بريدك الإلكتروني قبل تسجيل الدخول.',
      'auth.verificationEmailResent': 'تمت إعادة إرسال بريد التحقق بنجاح.',
      'auth.resendVerificationFailed': 'فشل في إعادة إرسال بريد التحقق.',
      'auth.forgotPassword': 'هل نسيت كلمة المرور؟',
      'auth.forgotPasswordDescription': 'أدخل بريدك الإلكتروني وسنرسل لك رابط إعادة تعيين كلمة المرور.',
      'auth.sendResetLink': 'إرسال رابط إعادة التعيين',
      'auth.resetRequestSent': 'إذا كان هناك حساب بهذا البريد الإلكتروني، فقد تم إرسال رابط إعادة تعيين كلمة المرور.',
      'auth.resetPassword': 'إعادة تعيين كلمة المرور',
      'auth.resetPasswordDescription': 'أدخل كلمة المرور الجديدة أدناه.',
      'auth.verifyingResetLink': 'جاري التحقق من رابط إعادة التعيين...',
      'auth.invalidOrExpiredResetToken': 'رابط إعادة التعيين غير صالح أو منتهي الصلاحية.',
      'auth.passwordResetSuccess': 'تمت إعادة تعيين كلمة المرور بنجاح. يمكنك تسجيل الدخول الآن.',
      'auth.passwordMinLength': 'يجب أن تتكون كلمة المرور من 6 أحرف على الأقل.',
      'auth.passwordsDoNotMatch': 'كلمتا المرور غير متطابقتين.',
      'auth.newPassword': 'كلمة المرور الجديدة',
      'auth.confirmNewPassword': 'تأكيد كلمة المرور الجديدة',
      'auth.networkError': 'خطأ في الشبكة. يرجى المحاولة مرة أخرى.',
      'auth.signingIn': 'جاري تسجيل الدخول...',
      'auth.loadingSignIn': 'يرجى الانتظار أثناء تسجيل الدخول',
      'auth.creatingAccount': 'جاري إنشاء الحساب...',
      'auth.passwordStrengthVeryWeak': 'ضعيفة جدًا',
      'auth.passwordStrengthWeak': 'ضعيفة',
      'auth.passwordStrengthFair': 'متوسطة',
      'auth.passwordStrengthGood': 'جيدة',
      'auth.passwordStrengthStrong': 'قوية',
      'auth.passwordStrengthLabel': 'قوة كلمة المرور',
      'auth.validationUsernameRequired': 'اسم المستخدم مطلوب',
      'auth.validationUsernameMin': 'يجب أن يكون اسم المستخدم 3 أحرف على الأقل',
      'auth.validationUsernameMax': 'يجب أن يكون اسم المستخدم أقل من 20 حرفًا',
      'auth.validationUsernamePattern': 'يمكن أن يحتوي اسم المستخدم على أحرف وأرقام وشرطة سفلية فقط',
      'auth.validationEmailRequired': 'البريد الإلكتروني مطلوب',
      'auth.validationEmailInvalid': 'يرجى إدخال بريد إلكتروني صالح',
      'auth.validationPhoneRequired': 'رقم الهاتف مطلوب',
      'auth.validationPhoneInvalid': 'يرجى إدخال رقم هاتف صالح',
      'auth.validationPhoneMin': 'يجب أن يتكون رقم الهاتف من 10 أرقام على الأقل',
      'auth.validationPhoneMax': 'يجب أن يكون رقم الهاتف أقل من 16 رقمًا',
      'auth.validationCountryRequired': 'البلد مطلوب',
      'auth.validationPasswordRequired': 'كلمة المرور مطلوبة',
      'auth.validationPasswordMin': 'يجب أن تتكون كلمة المرور من 6 أحرف على الأقل',
      'auth.validationConfirmPasswordRequired': 'يرجى تأكيد كلمة المرور',
      'auth.validationPasswordsMismatch': 'كلمتا المرور غير متطابقتين',
      'affiliateAuth.signIn': 'تسجيل دخول المسوق',
      'affiliateAuth.signInDescription': 'سجل دخولك إلى حساب المسوق الخاص بك',
      'affiliateAuth.noAccount': 'ليس لديك حساب مسوق؟',
      'affiliateAuth.haveAccount': 'لديك حساب مسوق بالفعل؟',
      'affiliateAuth.createAccount': 'إنشاء حساب مسوق',
      'affiliateAuth.createAccountDescription': 'انضم إلى برنامج التسويق بالعمولة وابدأ الربح',
      'affiliateAuth.error.emailNotVerified': 'يرجى التحقق من بريدك الإلكتروني قبل تسجيل الدخول. تحقق من بريدك الإلكتروني للحصول على رابط التحقق.',
      'affiliateAuth.error.pendingApproval': 'تم التحقق من بريدك الإلكتروني، لكن حساب التسويق بالعمولة الخاص بك لا يزال بانتظار موافقة الإدارة.',
      'affiliateAuth.error.rejected': 'تم رفض حساب التسويق بالعمولة الخاص بك. يرجى التواصل مع الدعم للمزيد من التفاصيل.',
      'affiliateAuth.error.suspended': 'تم تعليق حساب التسويق بالعمولة الخاص بك. يرجى التواصل مع الدعم.',
      'affiliateAuth.error.notActive': 'حساب التسويق بالعمولة الخاص بك غير نشط. يرجى التواصل مع الدعم.',
      'affiliateDashboard.title': 'لوحة تحكم المسوق',
      'affiliateDashboard.welcome': 'مرحبًا بعودتك، {{username}}!',
      'affiliateDashboard.loading': 'جاري تحميل لوحة التحكم...',
      'affiliateDashboard.loadError': 'فشل تحميل بيانات لوحة التحكم. يرجى المحاولة مرة أخرى.',
      'affiliateDashboard.nav.dashboard': 'لوحة التحكم',
      'affiliateDashboard.nav.payoutDetails': 'تفاصيل التحويل',
      'affiliateDashboard.expandSidebar': 'توسيع الشريط الجانبي',
      'affiliateDashboard.collapseSidebar': 'طي الشريط الجانبي',
      'affiliateDashboard.affiliate': 'مسوق',
      'affiliateDashboard.affiliateFallback': 'مسوق',
      'affiliateDashboard.userActions': 'إجراءات المستخدم',
      'affiliateDashboard.totalReferrals': 'إجمالي الإحالات',
      'affiliateDashboard.verified': 'تم التحقق',
      'affiliateDashboard.totalEarnings': 'إجمالي الأرباح',
      'affiliateDashboard.pendingEarnings': 'الأرباح المعلقة',
      'affiliateDashboard.totalPendingEarnings': 'إجمالي الأرباح المعلقة',
      'affiliateDashboard.totalConfirmedEarnings': 'إجمالي الأرباح المؤكدة',
      'affiliateDashboard.totalPaidEarnings': 'إجمالي الأرباح المدفوعة',
      'affiliateDashboard.yourReferralLink': 'رابط الإحالة الخاص بك',
      'affiliateDashboard.referralLinkSubtitle': 'شارك رمز الإحالة أو الرابط لدعوة مستخدمين جدد ومتابعة أرباحك.',
      'affiliateDashboard.commissionClarification': 'لكل مستخدم مسجل',
      'affiliateDashboard.referralCode': 'رمز الإحالة',
      'affiliateDashboard.referralLink': 'رابط الإحالة',
      'affiliateDashboard.commissionRate': 'نسبة العمولة',
      'affiliateDashboard.totalClicks': 'إجمالي النقرات',
      'affiliateDashboard.conversionRate': 'معدل التحويل',
      'affiliateDashboard.clickAnalytics30Days': 'تحليلات النقرات (آخر 30 يومًا)',
      'affiliateDashboard.recentReferrals': 'أحدث الإحالات',
      'affiliateDashboard.user': 'المستخدم',
      'affiliateDashboard.statusLabel': 'الحالة',
      'affiliateDashboard.commission': 'العمولة',
      'affiliateDashboard.referralDate': 'تاريخ الإحالة',
      'affiliateDashboard.confirmedDate': 'تاريخ التأكيد',
      'affiliateDashboard.paidDate': 'تاريخ الدفع',
      'affiliateDashboard.date': 'التاريخ',
      'affiliateDashboard.commissionHistory': 'سجل العمولات',
      'affiliateDashboard.amount': 'المبلغ',
      'affiliateDashboard.paymentMethod': 'طريقة الدفع',
      'affiliateDashboard.noReferralsYet': '!لا توجد إحالات بعد. شارك رابط الإحالة لبدء الربح',
      'affiliateDashboard.noCommissionHistory': '.لا يوجد سجل عمولات حتى الآن',
      'affiliateDashboard.status.pending': 'معلق',
      'affiliateDashboard.status.confirmed': 'مؤكد',
      'affiliateDashboard.status.paid': 'مدفوع',
      'affiliatePayout.title': 'تفاصيل التحويل',
      'affiliatePayout.subtitle': 'قم بإعداد الطريقة التي تريد استلام عمولاتك بها',
      'affiliatePayout.loading': 'جاري تحميل تفاصيل التحويل...',
      'affiliatePayout.loadError': 'فشل تحميل تفاصيل التحويل. يرجى المحاولة مرة أخرى.',
      'affiliatePayout.saveError': 'فشل حفظ تفاصيل التحويل. يرجى المحاولة مرة أخرى.',
      'affiliatePayout.savedSuccess': 'تم حفظ تفاصيل التحويل بنجاح!',
      'affiliatePayout.preferredPaymentMethod': 'طريقة الدفع المفضلة',
      'affiliatePayout.bankTransfer': 'تحويل بنكي',
      'affiliatePayout.bankTransferDescription': 'استلام المدفوعات مباشرة إلى حسابك البنكي',
      'affiliatePayout.paypal': 'باي بال',
      'affiliatePayout.paypalDescription': 'استلام المدفوعات عبر باي بال',
      'affiliatePayout.bankAccountDetails': 'تفاصيل الحساب البنكي',
      'affiliatePayout.accountHolderName': 'اسم صاحب الحساب',
      'affiliatePayout.accountHolderPlaceholder': 'محمد أحمد',
      'affiliatePayout.bankName': 'اسم البنك',
      'affiliatePayout.bankNamePlaceholder': 'بنك مثال',
      'affiliatePayout.accountNumber': 'رقم الحساب',
      'affiliatePayout.bankCountry': 'بلد البنك',
      'affiliatePayout.selectCountry': 'اختر البلد',
      'affiliatePayout.iban': 'رقم الآيبان',
      'affiliatePayout.swiftOptional': 'رمز SWIFT/BIC (اختياري)',
      'affiliatePayout.paypalDetails': 'تفاصيل باي بال',
      'affiliatePayout.paypalEmail': 'البريد الإلكتروني لباي بال',
      'affiliatePayout.saving': 'جاري الحفظ...',
      'affiliatePayout.savePayoutDetails': 'حفظ تفاصيل التحويل',
      'common.noResults': 'لا توجد نتائج',
      'common.error': 'خطأ',
      'common.tryAgain': 'حاول مرة أخرى',
      'common.copy': 'نسخ',
      'common.copied': 'تم النسخ!',
      'common.na': 'غير متوفر',
      'common.all': 'الكل',
      'common.month': 'الشهر',
      'common.closeMenu': 'إغلاق القائمة',
      'common.toggleMenu': 'تبديل القائمة',
      'auth.subscriptionRequired': 'اشتراك مطلوب',
      'auth.subscriptionRequiredMessage': 'للاستمرار في استخدام التطبيق، ستدفع 999 ريال للسنة الأولى، ثم 299 ريال كل سنة بعد ذلك.',
      'auth.subscriptionRequiredMessageRenewal': 'للاستمرار في استخدام التطبيق، ستدفع 299 ريال سنويًا.',
      'auth.annualPlan': 'اشتراك سنوي',
      'auth.subscriptionDetails': 'تفاصيل خطة الاشتراك',
      'auth.subscriptionClearCopy': 'ستدفع سعرًا واحدًا للسنة الأولى، ثم سعرًا ثابتًا أقل لكل سنة بعد ذلك.',
      'auth.firstYearLabel': 'السنة الأولى (يتم دفعها الآن)',
      'auth.firstYearDesc': 'دفعة واحدة تغطي أول 12 شهرًا.',
      'auth.afterFirstYearLabel': 'بعد السنة الأولى',
      'auth.afterFirstYearDescPrefix': 'يتم التجديد تلقائيًا بقيمة',
      'auth.afterFirstYearDescSuffix': 'سنويًا حتى تقوم بالإلغاء.',
      'auth.subscriptionExpired': 'انتهت صلاحية الاشتراك',
      'auth.subscriptionExpiredMessage': 'انتهت صلاحية اشتراكك. يرجى التجديد للمتابعة في استخدام التطبيق.',
      'auth.payNow': 'ادفع الآن',
      'auth.sessionExpired': 'انتهت الجلسة. يرجى تسجيل الدخول مرة أخرى.',
      'auth.completeSubscription': 'أكمل اشتراكك السنوي لاستخدام التطبيق.',
      'auth.loading': 'جاري التحميل...',
      'auth.apply': 'تطبيق',
      'auth.havePromo': 'هل لديك رمز ترويجي؟',
      'auth.enterPromo': 'أدخل الرمز الترويجي',
      'auth.invalidPromo': 'الرمز الترويجي غير صالح أو منتهي الصلاحية.',
      'auth.promoApplied': 'تم تطبيق الرمز: وفرت {{amount}} {{currency}}',
      'auth.promoOverridesSale': 'الرمز الترويجي يتجاوز الخصم العام النشط.',
      'auth.activeSaleCampaign': 'عرض خصم عام نشط',
      'auth.saleSavingsYearly': 'وفّر {{amount}} {{currency}} على الاشتراك السنوي',
      'auth.forOneYear': 'لمدة سنة واحدة',
      'auth.noAutoRenewal': 'لا يوجد تجديد تلقائي. يمكنك إعادة الاشتراك في السنة القادمة.',
      'auth.subscriptionAnnualBilled': 'تدفع {{amount}} {{currency}} مرة واحدة لمدة سنة.',
      'auth.paymentSuccess': 'تم الدفع بنجاح',
      'auth.paymentSuccessMessage': 'اشتراكك نشط. جاري التوجيه إلى لوحة التحكم...',
      'auth.paymentFailed': 'فشل الدفع أو تم الإلغاء',
      'auth.paymentFailedMessage': 'لم يتم إتمام الدفع. يمكنك المحاولة مرة أخرى من لوحة التحكم.',
      'auth.tryAgain': 'حاول مرة أخرى',
      'auth.paymentResult': 'نتيجة الدفع',
      'auth.paymentResultMessage': 'إذا أكملت الدفع، يجب أن يكون اشتراكك نشطاً. انتقل إلى لوحة التحكم للمتابعة.',
      
      // Pricing
      'pricing.title': 'تسعير الأطباق',
      'pricing.tab.pricing': 'التسعير',
      'pricing.inputs.rawMaterial': 'تكلفة المواد الخام',
      'pricing.inputs.fixedCost': 'التكلفة الثابتة',
      'pricing.inputs.variableCost': 'التكلفة المتغيرة',
      'pricing.inputs.profitMargin': 'هامش الربح (%)',
      'pricing.inputs.sellingPrice': 'سعر البيع',
      'pricing.inputs.marketPrice': 'السعر السوقي',
      'pricing.actions.reset': 'إعادة تعيين',
      'pricing.actions.autoCalculate': 'حساب تلقائي',
      'pricing.actions.save': 'حفظ التسعير',
      'pricing.actions.openInsights': 'فتح رؤى الربحية',
      'pricing.totals.totalCost': 'التكلفة الإجمالية',
      'pricing.totals.rawMaterialPercent': 'نسبة المواد الخام %',
      'pricing.totals.fixedCostPercent': 'نسبة التكلفة الثابتة %',
      'pricing.totals.variableCostPercent': 'نسبة التكلفة المتغيرة %',
      'pricing.messages.enterOneOfMarginOrPrice': 'يرجى إدخال إما هامش الربح أو سعر البيع للحساب.',
      'pricing.messages.negativeMargin': 'التكاليف تتجاوز سعر البيع (هامش ربح سلبي).',
      'pricing.messages.pricingSaved': 'تم حفظ التسعير بنجاح',
      'pricing.messages.pricingUpdated': 'تم تحديث التسعير بنجاح',
      'pricing.messages.pricingDeleted': 'تم حذف التسعير بنجاح',
      'pricing.marketComparison.above': 'أعلى من السوق',
      'pricing.marketComparison.below': 'أقل من السوق',
      'pricing.marketComparison.equal': 'مساوي للسوق',
      'pricing.validation.marginRequired': 'يرجى إدخال هامش الربح',
      'pricing.validation.priceRequired': 'يرجى إدخال سعر البيع',
      'pricing.validation.invalidMargin': 'هامش الربح يجب أن يكون بين 0% و 95%',
      'pricing.validation.invalidPrice': 'سعر البيع يجب أن يكون أكبر من 0',
      
      // Currency Settings
      'currency.title': 'الإعدادات',
      'currency.current': 'العملة الحالية',
      'currency.select': 'اختر العملة',
      'currency.preview': 'معاينة التنسيق',
      'currency.decimalPlaces': 'المنازل العشرية',
      'currency.example': 'مثال',
      
      // Additional translations from dictionary
      'app.name': 'سكيل فلو',
      'app.version': 'الإصدار 1.0.0',
      'app.description': 'إدارة التكاليف',
      'common.bulkDelete': 'حذف',
      'common.materials': 'المواد',
      'materials.materials': 'مواد',
      'common.allCategories': 'كل التصنيفات',
      'common.allMaterials': 'كل المواد',
      'common.allRecipes': 'كل الأطباق',
      'common.dishes': 'الوصفات',
      'common.dish': 'وصفة',
      'common.categoriesTotal': 'مجموع التصنيفات',
      'common.materialsTotal': 'مجموع المواد',
      'common.clickToViewMaterials': 'اضغط لعرض المواد',
      'common.clickToViewDetails': 'اضغط لعرض التفاصيل',
      'common.none': 'لا يوجد',
      'common.noAllergens': 'لا يوجد مسببات للحساسية',
      'common.noAllergensFound': 'لا توجد مسببات حساسية مطابقة لـ "{searchTerm}"',
      'common.allergens': 'مسببات حساسية',
      'common.allergen': 'مسبب حساسية',
      'common.loading': 'جاري التحميل',
      'common.error': 'خطأ',
      'common.success': 'تم بنجاح',
      'common.print': 'طباعة',
      'common.at': 'في',
      'common.goTo': 'انتقل إلى',
      'common.noCategoriesFound': 'لم يتم العثور على تصنيفات',
      'common.noCategoriesYet': 'لا توجد تصنيفات بعد',
      'common.tryAdjustingSearch': 'حاول تعديل مصطلحات البحث',
      'common.createFirstCategory': 'إنشاء أول تصنيف',
      'common.deleteCategory': 'حذف التصنيف',
      'common.cannotDeleteCategory': 'تعذر حذف التصنيف',
      'common.deleteMaterial': 'حذف المادة',
      'common.deleteRecipe': 'حذف الوصفة',
      'common.confirmDeleteCategory': 'هل أنت متأكد من حذف التصنيف "{{name}}"؟',
      'common.confirmDeleteMaterial': 'هل أنت متأكد من حذف المادة "{{name}}"؟',
      'common.confirmDeleteRecipe': 'هل أنت متأكد من حذف الوصفة "{{name}}"؟',
      'common.confirmDeleteSelectedMaterials': 'هل أنت متأكد من حذف {{count}} مواد محددة؟ لا يمكن التراجع عن هذا الإجراء.',
      'common.confirmDeleteSelectedCategories': 'هل أنت متأكد من حذف {{count}} تصنيفات محددة؟ لا يمكن التراجع عن هذا الإجراء.',
      'common.confirmDeleteSelectedRecipes': 'هل أنت متأكد من حذف {{count}} وصفات محددة؟ لا يمكن التراجع عن هذا الإجراء.',
      'common.language': 'اللغة',
      'common.switchToArabic': 'التبديل إلى العربية',
      'common.switchToEnglish': 'التبديل إلى الإنجليزية',
      
      // Allergens
      'allergens.wheat': 'القمح',
      'allergens.wheat.examples': 'يحتوي على الغلوتين، مثل: القمح، الجاودار، الشعير، الشوفان',
      'allergens.fish': 'الأسماك ومنتجاتها',
      'allergens.fish.examples': 'مثل: لحم السمك، زيت السمك',
      'allergens.peanuts': 'الفول السوداني ومنتجاته',
      'allergens.peanuts.examples': 'مثل: زبدة الفول السوداني',
      'allergens.tree_nuts': 'المكسرات ومنتجاتها',
      'allergens.tree_nuts.examples': 'مثل: الكاجو، الفستق، وغيرها',
      'allergens.milk': 'الحليب ومنتجاته',
      'allergens.milk.examples': '(بما في ذلك اللاكتوز) مثل: الحليب، الزبادي، الجبن، الكريمة، الزبدة',
      'allergens.celery': 'الكرفس ومنتجاته',
      'allergens.celery.examples': 'مثل: بذور الكرفس، ملح الكرفس',
      'allergens.crustaceans': 'القشريات ومنتجاتها',
      'allergens.crustaceans.examples': 'مثل: الجمبري',
      'allergens.eggs': 'البيض ومنتجاته',
      'allergens.eggs.examples': 'مثل: البيض المجفف',
      'allergens.mustard': 'الخردل ومنتجاته',
      'allergens.mustard.examples': 'مثل: بذور الخردل، زيت الخردل، صلصة الخردل',
      'allergens.sesame': 'بذور السمسم ومنتجاته',
      'allergens.sesame.examples': 'مثل: زيت السمسم',
      'allergens.mollusks': 'الرخويات ومنتجاتها',
      'allergens.mollusks.examples': 'مثل: المحار (البطلينوس، الأسقلوب)',
      'allergens.lupin': 'الترمس ومنتجاته',
      'allergens.lupin.examples': 'مثل: دقيق الترمس',
      'allergens.soybeans': 'فول الصويا ومنتجاته',
      'allergens.soybeans.examples': 'مثل: حليب الصويا',
      'allergens.sulfites': 'ثاني أكسيد الكبريت والكبريتات',
      'allergens.sulfites.examples': 'بتركيزات أكبر من 10 مجم/كجم أو أكثر',
      
      // Allergen shortcuts
      'allergens.short.wheat': 'قمح',
      'allergens.short.fish': 'سمك',
      'allergens.short.peanuts': 'فول سوداني',
      'allergens.short.tree_nuts': 'مكسرات',
      'allergens.short.milk': 'حليب',
      'allergens.short.celery': 'كرفس',
      'allergens.short.crustaceans': 'قشريات',
      'allergens.short.eggs': 'بيض',
      'allergens.short.mustard': 'خردل',
      'allergens.short.sesame': 'سمسم',
      'allergens.short.mollusks': 'رخويات',
      'allergens.short.lupin': 'ترمس',
      'allergens.short.soybeans': 'صويا',
      'allergens.short.sulfites': 'كبريتات',
      
      // Material Details
      'materials.materialOverview': 'نظرة عامة على المادة',
      'materials.basicInformation': 'المعلومات الأساسية',
      'materials.nutritionalInformation': 'المعلومات الغذائية',
      'materials.specifications': 'المواصفات',
      'materials.costBreakdown': 'تفصيل التكلفة',
      'materials.usageStatistics': 'إحصائيات الاستخدام',
      'materials.recipeDetails': 'تفاصيل الوصفات',
      'materials.mainUnit': 'الوحدة الرئيسية',
      'materials.subUnit': 'الوحدة الفرعية',
      'materials.qtyPerMainUnit': 'الكمية لكل وحدة رئيسية',
      'materials.qtyPerPiece': 'الكمية لكل قطعة',
      'materials.priceWithoutVat': 'السعر بدون ضريبة القيمة المضافة',
      'materials.priceWithVat': 'السعر مع ضريبة القيمة المضافة',
      'materials.calculatedUnitCost': 'تكلفة الوحدة المحسوبة',
      'materials.usedInRecipes': 'مستخدم في الأطباق',
      'materials.totalQuantityUsed': 'إجمالي الكمية المستخدمة',
      'materials.primaryUnitOfMeasurement': 'الوحدة الأساسية للقياس',
      'materials.secondaryUnitOfMeasurement': 'الوحدة الثانوية للقياس',
      'materials.quantityInMainUnit': 'الكمية في الوحدة الرئيسية',
      'materials.quantityPerIndividualPiece': 'الكمية لكل قطعة فردية',
      'materials.priceExcludingVat': 'السعر بدون ضريبة القيمة المضافة',
      'materials.priceIncludingVat': 'السعر مع ضريبة القيمة المضافة (15%)',
      'materials.costPerSmallestUnit': 'التكلفة لكل أصغر وحدة',
      'materials.howThisMaterialIsUsed': 'كيفية استخدام هذه المادة في الوصفات',
      'materials.basicInformationAndSpecifications': 'المعلومات الأساسية والمواصفات',
      'materials.measurementAndQuantityDetails': 'تفاصيل القياس والكمية',
      'materials.detailedCostAnalysisAndCalculations': 'تحليل التكلفة التفصيلي والحسابات',
      'materials.available': 'متوفر',
      'materials.noRecipesFound': 'لم يتم العثور على أطباق',
      'materials.noMaterialsFound': 'لم يتم العثور على مواد',
      'materials.noMaterialsAddedYet': 'لم يتم إضافة أي مواد بعد.',
      'materials.generatedOn': 'تم إنشاؤه في',
      'materials.dishCostingSystem': 'نظام حساب تكلفة الأطباق - تفاصيل المادة',
      'materials.pageOf': 'صفحة 1 من 1',
      'common.edit': 'تعديل',
      'common.delete': 'حذف',
      'common.searchByDishNameOrId': 'البحث حسب اسم التصنيف أو المعرّف',
      'common.searchByCategoryNameOrId': 'البحث حسب اسم التصنيف أو المعرّف',
      'common.searchByMaterialNameOrId': 'البحث حسب اسم المادة أو المعرّف',
      'common.materialInformation': 'معلومات المادة',
      'common.basicDetails': 'تفاصيل أساسية عن المادة',
      'common.nutritionalInformation': 'المعلومات الغذائية',
      'common.calorieAndServingInfo': 'معلومات السعرات وحجم الحصة',
      'common.servingSize': 'حجم الحصة',
      'common.caloriesPerServing': 'السعرات (لكل حصة)',
      'common.allergens': 'مسببات الحساسية',
      'common.selectAllergens': 'اختر مسببات الحساسية…',
      'common.materialImage': 'صورة المادة',
      'common.uploadImage': 'رفع صورة',
      'common.uploading': 'جاري الرفع...',
      'common.cancel': 'إلغاء',
      'common.removeImage': 'إزالة الصورة',
      'common.selectUnit': 'اختر الوحدة',
      'common.selectCategory': 'اختر التصنيف',
      'common.selectCategoryForDeletion': 'اختر التصنيف للحذف',
      'materials.subtitle': 'اختر تصنيف لعرض وإدارة المواد والمستلزمات',
      'common.enterPriceExcludingVat': 'أدخل السعر بدون الضريبة',
      'common.calculatedAutomatically': 'محسوب تلقائيًا',
      'common.pleaseSelectImageFile': 'يرجى اختيار ملف صورة',
      'common.imageSizeLimit': 'يجب أن يكون حجم الصورة أقل من 2 ميجابايت',
      'common.failedToPreviewImage': 'فشل في معاينة الصورة',
      'common.failedToUploadImage': 'فشل في رفع الصورة. يرجى المحاولة مرة أخرى.',
      'common.pleaseEnterValidPositiveNumber': 'يرجى إدخال رقم صحيح موجب',
      'common.pleaseEnterValidNumber': 'يرجى إدخال رقم صحيح (يمكن أن يكون 0 أو موجب)',
      'common.pleaseEnterValidNumberZeroOrPositive': 'يرجى إدخال رقم صحيح (0 أو موجب)',
      'common.thisDefinesBaseUnit': 'هذا يحدد الوحدة الأساسية التي تُقاس بها السعرات',
      'common.willBeDisplayedAsWholeNumbers': 'ستُعرض كأرقام صحيحة (مثل: 18.5 → 19 سعرة)',
      'common.selectAllergensPresent': 'اختر أي مسببات حساسية موجودة في هذه المادة. سيتم تمييز أي وصفات تستخدم هذه المادة تلقائيًا.',
      
      // Bulk delete
      'common.selectAll': 'اختيار الكل',
      'common.deselectAll': 'إلغاء اختيار الكل',
      'common.deleteSelected': 'حذف المحدد',
      'common.deleteSelectedMaterialsCount': 'حذف المواد المحددة ({{count}})',
      'common.deleteSelectedCategoriesCount': 'حذف التصنيفات المحددة ({{count}})',
      'common.bulkDelete': 'حذف متعدد',
      'common.confirmBulkDelete': 'تأكيد الحذف المتعدد',
      
      'common.clickToView': 'اضغط لعرض المواد',
      'common.sortById': 'الترتيب',
      'common.excelImportExport': 'استيراد/تصدير إكسل',
      'common.materialInformation': 'معلومات المادة',
      'common.basicDetails': 'تفاصيل أساسية عن المادة',
      'common.priceWithoutVat': 'السعر بدون ضريبة القيمة المضافة',
      'common.priceWithVat': 'السعر مع ضريبة القيمة المضافة',
      'common.enterPriceExcludingVat': 'أدخل السعر بدون الضريبة',
      'common.calculatedAutomatically': 'محسوب تلقائيًا',
      'common.vatCalculation': 'احتساب الضريبة',
      'common.nutritionalInformation': 'المعلومات الغذائية',
      'common.calorieAndServingInfo': 'معلومات السعرات وحجم الحصة',
      'common.servingSize': 'حجم الحصة',
      'common.caloriesPerServing': 'السعرات (لكل حصة)',
      'common.allergens': 'مسببات الحساسية',
      'common.selectAllergens': 'اختر مسببات الحساسية…',
      'common.materialImage': 'صورة المادة',
      'common.uploadImageForMaterial': 'ارفع صورة للمادة',
      'common.uploadImage': 'رفع صورة',
      'common.recipeOverview': 'نظرة عامة على الوصفة',
      'common.procedure': 'الطريقة / الخطوات',
      'common.recipeDetails': 'تفاصيل الوصفة',
      'common.portionUnit': 'وحدة الحصة',
      'common.holdingTemperature': 'درجة حرارة الحفظ (°م)',
      'common.hasPricing': 'يحتوي على تسعير',
      'common.enablePricing': 'تفعيل التسعير',
      'common.recipeImage': 'صورة الطبق',
      'common.chooseImage': 'اختر صورة',
      'common.imageRequirements': 'متطلبات الصورة',
      'common.formats': 'الصيغ: JPG, PNG, GIF',
      'common.nextIngredients': 'التالي: المكونات',
      'common.ingredientsLibrary': 'مكتبة المكونات',
      'common.allCategories': 'كل التصنيفات',
      'common.allMaterials': 'كل المواد',
      'common.allRecipes': 'كل الأطباق',
      'common.tip': 'ملاحظة',
      'common.selectedIngredients': 'المكونات المختارة',
      'common.noIngredientsAdded': 'لم تتم إضافة أي مكونات بعد',
      'common.backToRecipeOverview': 'رجوع إلى نظرة عامة على الوصفة',
      'common.nextProcedure': 'التالي: الخطوات',
      'common.addProcedureSteps': 'إضافة خطوات الوصفة',
      'common.instruction': 'التعليمات',
      'common.enterStepInstruction': 'أدخل التعليمات الخاصة بالخطوة…',
      'common.stepImage': 'صورة الخطوة',
      'common.addStep': 'إضافة خطوة',
      'common.noProcedureSteps': 'لم تتم إضافة أي خطوات بعد',
      'common.addStepsUsingForm': 'أضف الخطوات باستخدام النموذج أعلاه',
      'common.backToIngredients': 'رجوع إلى المكونات',
      'common.manageAllocatedFixedExpense': 'إدارة بنود التكاليف الثابتة وتوزيعها حسب التصنيف',
      'common.enterAllocatedFixedExpenseItems': 'إدخال بنود التكاليف الثابتة',
      'common.distributeAllocatedFixedCosts': 'توزيع التكاليف الثابتة حسب التصنيف',
      'common.totalMonthlyValue': 'القيمة الشهرية الإجمالية',
      'common.totalYearlyValue': 'القيمة السنوية الإجمالية',
      'common.allocatedFixedExpenseItems': 'بنود التكاليف الثابتة',
      'common.itemsList': 'قائمة البنود',
      'common.searchByItemName': 'البحث حسب اسم البند…',
      'common.itemNameEn': 'اسم البند (بالإنجليزية)',
      'common.itemNameAr': 'اسم البند (بالعربية)',
      'common.addNewItem': 'إضافة بند جديد',
      'common.costDistributionResults': 'نتائج توزيع التكاليف',
      'common.totalAllocated': 'المجموع الموزع',
      'common.allocatedVariableCosts': 'التكاليف المتغيرة',
      'common.manageAllocatedVariableExpense': 'إدارة بنود التكاليف المتغيرة وتوزيعها حسب التصنيف.',
      'common.enterAllocatedVariableExpenseItems': 'إدخال بنود التكاليف المتغيرة',
      'common.distributeAllocatedVariableCosts': 'توزيع التكاليف المتغيرة حسب التصنيف',
      'common.allocatedVariableExpenseItems': 'بنود التكاليف المتغيرة',
      'common.managePricingForRecipes': 'إدارة تسعير الأطباق وتحليل الربحية',
      'common.createRecipe': 'إنشاء وصفة',
      'common.searchByDishNameOrId': 'البحث حسب اسم الطبق أو المعرّف',
      'common.dishes': 'الأطباق',
      'common.dish': 'الطبق',
      'common.dishCostPerServing': 'تكلفة الطبق لكل حصة',
      'common.fixedCost': 'التكلفة الثابتة',
      'common.variableCost': 'التكلفة المتغيرة',
      'common.totalCost': 'التكلفة الإجمالية',
      'common.profitMargin': 'هامش الربح',
      'common.sellingPrice': 'سعر البيع',
      'common.marketPrice': 'السعر السوقي',
      'common.pricingStatus': 'حالة التسعير',
      'common.activeUser': 'مستخدم نشط',

      // Allocated Fixed Costs
      'allocatedFixedCosts.title': 'توزيع التكاليف الثابتة',
      'allocatedFixedCosts.description': 'إدارة بنود التكاليف الثابتة وتوزيع التكاليف حسب التصنيف.',
      'allocatedFixedCosts.enterItems': 'إدخال بنود التكاليف الثابتة',
      'allocatedFixedCosts.distributeCosts': 'توزيع التكاليف الثابتة حسب التصنيف',
      'allocatedFixedCosts.addNewItem': 'إضافة بند جديد',
      'allocatedFixedCosts.editItem': 'تعديل بند التكلفة الثابتة',
      'allocatedFixedCosts.itemNameEn': 'اسم البند (بالإنجليزية)',
      'allocatedFixedCosts.itemNameAr': 'اسم البند (بالعربية)',
      'allocatedFixedCosts.itemNameEnPlaceholder': 'أدخل اسم البند بالإنجليزية',
      'allocatedFixedCosts.itemNameArPlaceholder': 'أدخل اسم العنصر بالعربية',
      'allocatedFixedCosts.monthlyValue': 'القيمة الشهرية',
      'allocatedFixedCosts.yearlyValue': 'القيمة السنوية',
      'allocatedFixedCosts.monthlyValuePlaceholder': 'أدخل القيمة الشهرية',
      'allocatedFixedCosts.yearlyValuePlaceholder': 'أدخل القيمة السنوية',
      'allocatedFixedCosts.fixedExpenseItems': 'بنود التكاليف الثابتة',
      'allocatedFixedCosts.totalMonthlyValue': 'إجمالي القيمة الشهرية',
      'allocatedFixedCosts.totalYearlyValue': 'إجمالي القيمة السنوية',
      'allocatedFixedCosts.costDistributionResults': 'نتائج توزيع التكاليف',
      'allocatedFixedCosts.category': 'التصنيف',
      'allocatedFixedCosts.categoryId': 'معرف التصنيف',
      'allocatedFixedCosts.allocatedPercentage': 'النسبة الموزعة %',
      'allocatedFixedCosts.expectedOrders': 'الطلبات المتوقعة',
      'allocatedFixedCosts.costPerDish': 'التكلفة لكل طبق (شهرياً)',
      'allocatedFixedCosts.totalAllocated': 'إجمالي الموزع',
      'allocatedFixedCosts.totalAllocationExceeds': 'يتجاوز التوزيع الإجمالي 100%',
      'allocatedFixedCosts.currentTotal': 'المجموع الحالي',
      'allocatedFixedCosts.reduceAllocations': 'يرجى تقليل التوزيعات للبقاء ضمن 100%.',
      'allocatedFixedCosts.edit': 'تعديل',
      'allocatedFixedCosts.save': 'حفظ',
      'allocatedFixedCosts.cancel': 'إلغاء',
      'allocatedFixedCosts.addItem': 'إضافة بند',
      'allocatedFixedCosts.updateItem': 'تحديث البند',
      'allocatedFixedCosts.saving': 'جاري الحفظ...',
      'allocatedFixedCosts.adding': 'جاري الإضافة...',
      'allocatedFixedCosts.deleteItem': 'حذف البند',
      'allocatedFixedCosts.deleteDistribution': 'حذف التوزيع',
      'allocatedFixedCosts.deleting': 'جاري الحذف...',
      'allocatedFixedCosts.deleteItemConfirm': 'هل أنت متأكد من أنك تريد حذف البند "{itemName}"؟ لا يمكن التراجع عن هذا الإجراء.',
      'allocatedFixedCosts.deleteDistributionConfirm': 'هل أنت متأكد من أنك تريد حذف التوزيع للتصنيف "{categoryName}"؟ لا يمكن التراجع عن هذا الإجراء.',
      'allocatedFixedCosts.searchByItemName': 'البحث حسب اسم البند...',
      'allocatedFixedCosts.searchByCategoryName': 'البحث حسب اسم التصنيف...',
      'allocatedFixedCosts.sortById': 'ترتيب حسب المعرف',
      'allocatedFixedCosts.sortByName': 'ترتيب حسب الاسم',
      'allocatedFixedCosts.sortBy': 'ترتيب حسب',
      'allocatedFixedCosts.validationError': 'خطأ في التحقق',
      'allocatedFixedCosts.allocationLimitExceeded': 'تم تجاوز حد التوزيع',
      'allocatedFixedCosts.totalAllocatedCannotExceed': 'لا يمكن أن يتجاوز إجمالي النسبة الموزعة 100%. سيكون المجموع الحالي {{total}}%',
      'allocatedFixedCosts.itemsList': 'قائمة البنود',

      // Allocated Variable Costs
      'allocatedVariableCosts.title': 'توزيع التكاليف المتغيرة',
      'allocatedVariableCosts.description': 'إدارة بنود التكاليف المتغيرة وتوزيع التكاليف حسب التصنيف.',
      'allocatedVariableCosts.enterItems': 'إدخال بنود التكاليف المتغيرة',
      'allocatedVariableCosts.distributeCosts': 'توزيع التكاليف المتغيرة حسب التصنيف',
      'allocatedVariableCosts.addNewItem': 'إضافة بند جديد',
      'allocatedVariableCosts.editItem': 'تعديل بند التكلفة المتغيرة',
      'allocatedVariableCosts.itemNameEn': 'اسم البند (بالإنجليزية)',
      'allocatedVariableCosts.itemNameAr': 'اسم البند (بالعربية)',
      'allocatedVariableCosts.itemNameEnPlaceholder': 'أدخل اسم البند بالإنجليزية',
      'allocatedVariableCosts.itemNameArPlaceholder': 'أدخل اسم العنصر بالعربية',
      'allocatedVariableCosts.monthlyValue': 'القيمة الشهرية',
      'allocatedVariableCosts.yearlyValue': 'القيمة السنوية',
      'allocatedVariableCosts.monthlyValuePlaceholder': 'أدخل القيمة الشهرية',
      'allocatedVariableCosts.yearlyValuePlaceholder': 'أدخل القيمة السنوية',
      'allocatedVariableCosts.variableExpenseItems': 'بنود التكاليف المتغيرة',
      'allocatedVariableCosts.totalMonthlyValue': 'إجمالي القيمة الشهرية',
      'allocatedVariableCosts.totalYearlyValue': 'إجمالي القيمة السنوية',
      'allocatedVariableCosts.costDistributionResults': 'نتائج توزيع التكاليف',
      'allocatedVariableCosts.category': 'التصنيف',
      'allocatedVariableCosts.categoryId': 'معرف التصنيف',
      'allocatedVariableCosts.allocatedPercentage': 'النسبة الموزعة %',
      'allocatedVariableCosts.expectedOrders': 'الطلبات المتوقعة',
      'allocatedVariableCosts.costPerDish': 'التكلفة لكل طبق (شهرياً)',
      'allocatedVariableCosts.totalAllocated': 'إجمالي الموزع',
      'allocatedVariableCosts.totalAllocationExceeds': 'يتجاوز التوزيع الإجمالي 100%',
      'allocatedVariableCosts.currentTotal': 'المجموع الحالي',
      'allocatedVariableCosts.reduceAllocations': 'يرجى تقليل التوزيعات للبقاء ضمن 100%.',
      'allocatedVariableCosts.edit': 'تعديل',
      'allocatedVariableCosts.save': 'حفظ',
      'allocatedVariableCosts.cancel': 'إلغاء',
      'allocatedVariableCosts.addItem': 'إضافة بند',
      'allocatedVariableCosts.updateItem': 'تحديث البند',
      'allocatedVariableCosts.saving': 'جاري الحفظ...',
      'allocatedVariableCosts.adding': 'جاري الإضافة...',
      'allocatedVariableCosts.deleteItem': 'حذف البند',
      'allocatedVariableCosts.deleteDistribution': 'حذف التوزيع',
      'allocatedVariableCosts.deleting': 'جاري الحذف...',
      'allocatedVariableCosts.deleteItemConfirm': 'هل أنت متأكد من أنك تريد حذف البند "{itemName}"؟ لا يمكن التراجع عن هذا الإجراء.',
      'allocatedVariableCosts.deleteDistributionConfirm': 'هل أنت متأكد من أنك تريد حذف التوزيع للتصنيف "{categoryName}"؟ لا يمكن التراجع عن هذا الإجراء.',
      'allocatedVariableCosts.searchByItemName': 'البحث حسب اسم البند...',
      'allocatedVariableCosts.searchByCategoryName': 'البحث حسب اسم التصنيف...',
      'allocatedVariableCosts.sortById': 'ترتيب حسب المعرف',
      'allocatedVariableCosts.sortByName': 'ترتيب حسب الاسم',
      'allocatedVariableCosts.sortBy': 'ترتيب حسب',
      'allocatedVariableCosts.validationError': 'خطأ في التحقق',
      'allocatedVariableCosts.allocationLimitExceeded': 'تم تجاوز حد التوزيع',
      'allocatedVariableCosts.totalAllocatedCannotExceed': 'لا يمكن أن يتجاوز إجمالي النسبة الموزعة 100%. سيكون المجموع الحالي {{total}}%',
      'allocatedVariableCosts.itemsList': 'قائمة البنود',
      'allocatedVariableCosts.actions': 'الإجراءات',
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: false,
    interpolation: {
      escapeValue: false,
    },
  });

export default i18n; 